import { Device } from 'mediasoup-client';
import { EventRegister } from 'react-native-event-listeners';
import { recorder } from './browserRecorder';
import { v4 as uuidv4 } from 'uuid';
import { VIDEO_LADDER, TOP_RUNG, MAX_HEIGHT,
         encodingsForCapture, screenEncodingsForCapture, singleEncodingTarget,
         rungForSpeed, codecOptionsForRung, labelForHeight,
         bitrateStepsFor, BUDGET_DOWN_VOTES, BUDGET_UP_VOTES,
         DOWNSCALE_TOLERANCE, RUNG_SETTLE_MS,
         isWeakLine, WEAK_LINE_BPS, RECOVERY_MARGIN, RECOVERY_SAMPLES,
         WEAK_PROMPT_MS, WEAK_PROMPT_COOLDOWN_MS,
         WEAK_PROMPT_RATIO, WEAK_PROMPT_MIN_SAMPLES,
         CPU_EASED_FPS, CPU_EASE_VOTES, CPU_RESTORE_VOTES } from './videoLadder';
import { createBoardStream } from './boardCapture';

const mediaType = {
    audio: 'audioType',
    video: 'videoType',
    screen: 'screenType'
}
const _EVENTS = {
    exitRoom: 'exitRoom',
    openRoom: 'openRoom',
    startVideo: 'startVideo',
    stopVideo: 'stopVideo',
    startAudio: 'startAudio',
    stopAudio: 'stopAudio',
    startScreen: 'startScreen',
    stopScreen: 'stopScreen'
}
let interval = null;
const allmediaConstraints = {
    'audioType': {
        audio: true,
        video: false
    },
    'videoType': {
        // No microphone here. produce() takes getVideoTracks()[0] from this stream and the
        // audio track it used to ask for was never read by anything — but it stayed live,
        // so a camera broadcast held two independent captures of the same microphone: this
        // one and the real audioType producer. Two capture sessions on one device, each
        // running its own echo cancellation, gain control and noise suppression, is what
        // made the audio crackle and break up. swapVideoToCamera re-runs these constraints
        // on every camera on/off, so it got worse each time as well.
        audio: false,
        video: {
            width: {
                ideal: 1920
            },
            height: {
                ideal: 1080
            },
            aspectRatio: {
                ideal: 1.7777777778
            },
            frameRate: { ideal: 30 }
        }
    },
    'screenType': { 
        audio: false,
        video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
        }
    }
}
let animationFrame = false
let innerWidth = window.innerWidth;
function updateSize(){
    innerWidth = window.innerWidth
}
class RoomClient {

    constructor({snackbar, localMediaEl, remoteVideoEl, remoteAudioEl, mediasoupClient, socket, user_id, room_id, user, successCallback, eventCallback, refer=false, fileName=null}) {
        // The test broadcast server validates user_id as a string (/^[A-Za-z0-9._-]{1,128}$/)
        // and rejects the numeric session id outright, which left join unanswered and the
        // class on the connecting spinner. Prod accepts either, so the string goes to both.
        this.user_id = `${user_id}`
        this.user = user
        this.snackbar = snackbar
        this.localMediaEl = localMediaEl
        this.remoteVideoEl = remoteVideoEl
        this.remoteAudioEl = remoteAudioEl
        this.mediasoupClient = mediasoupClient

        this.socket = socket
        this.producerTransport = null
        this.consumerTransport = null
        this.device = null
        this.room_id = room_id
        this.activeSpeaker = null
        this.offline = false
        this.eventCallback = eventCallback;
        this.refer = refer
        this.audioStream = null
        this.videoStream = null
        this.processId = null
        window.addEventListener("resize", updateSize);
        this.consumers = new Map()
        this.producers = new Map()
        this.producerStats = new Map()
        this.consumerStats = new Map()
        this.producerLabel = new Map()
        this.allTracks = new Map()
        // ---- adaptive bitrate ----------------------------------------------------------
        // Last measured line speed, pushed in from the speed test in the header. null until
        // it lands, which rungForSpeed reads as "start in the middle and climb".
        this._lineSpeedBps = null
        // Rung the camera producer is currently allowed to reach, as an index into
        // VIDEO_LADDER. Held here rather than read back off the producer because
        // setMaxSpatialLayer is a request, and comparing against what we last asked for is
        // what stops the controller reissuing the same call every two seconds.
        this._videoRung = null
        // Consecutive samples arguing for a change. A single bad sample is a hiccup; the
        // counters are what make this a controller rather than a trigger.
        this._rungDownVotes = 0
        this._rungUpVotes = 0
        // When the ladder last moved. An encoder takes a second or two to bring a newly
        // activated layer up to size, and during that stretch the frame is legitimately
        // smaller than the rung it belongs to — read as a shortfall, that would step back
        // down immediately and leave the ladder oscillating over its own ramp-up.
        this._rungChangedAt = 0
        // The rung AUTO was on when a screen share took over, held so the swap back can resume
        // it. null whenever no share is in progress.
        this._rungBeforeShare = null
        // ---- the budget, for producers with no ladder ----------------------------------
        // Where a locked single encoding currently sits in its step table: 0 is nominal and
        // the most expensive, the last index is the floor. This is the only adaptation a
        // camera has under LOCK_SINGLE_ENCODING, and the only one a screen share or
        // whiteboard has ever had, because all three stand the ladder down on purpose.
        //
        // Index rather than a bitrate, because the table it indexes changes with the
        // content: a screen share and a camera want opposite things at the same step. See
        // bitrateStepsFor.
        this._budgetIndex = 0
        // Whether the video producer is currently carrying detail-first content — a shared
        // screen or the whiteboard — which decides both the step table and the size the
        // controller expects to see going out. Recorded by _tuneVideoSender, which is the
        // one place that learns of a swap.
        this._sendingScreen = false
        // Consecutive samples arguing each way, and when the budget last moved. The same
        // shape as the ladder's counters, and for the same reason: a single bad sample is a
        // hiccup, and an encoder needs a moment to settle into a new bitrate before its
        // transient can be read as evidence about the line.
        this._budgetDownVotes = 0
        this._budgetUpVotes = 0
        this._budgetChangedAt = 0
        // The capture getUserMedia actually handed over. A 240p webcam can never fill the
        // 360p rung, and without this every frame it sends reads as the line failing.
        this._captureHeight = null
        // Weak-line lock. Set when the measured line cannot carry the second rung, or when
        // the bottom rung is itself failing; while it holds, Auto may not climb above 360p
        // at all — not one rung at a time, not on a flattering bandwidth estimate. Released
        // only by RECOVERY_SAMPLES consecutive clean samples on a line measured clear of the
        // threshold, which is what "until the network is stable" comes down to.
        this._weakLocked = false
        // Consecutive samples with nothing wrong with them, counting towards that release.
        this._steadyVotes = 0
        // Whether the frame rate is currently being held down to spare a machine that
        // cannot encode fast enough, and the samples arguing each way. Separate from every
        // counter above because this is not a network state: the ladder does not move for
        // it, and a class held at 15fps for CPU is not a class on a bad line.
        this._cpuEased = false
        this._cpuVotes = 0
        this._cpuClearVotes = 0
        // The last WEAK_PROMPT_MS of samples as {at, strained}, and when the "turn your
        // camera off" prompt last went out. These feed the prompt, not the ladder. A window
        // rather than a stopwatch, because the connection that ruins a class is the one that
        // keeps almost recovering — see WEAK_PROMPT_RATIO.
        this._strainWindow = []
        // When the current stretch of publishing began, so a window that is merely short is
        // never mistaken for a window that is clean.
        this._healthSince = 0
        this._weakPromptedAt = 0
        // Quality the viewer picked from the menu, as a ladder index; null means Auto. A
        // pin is a floor as well as a ceiling — someone who chooses 360p is asking for 360p,
        // not for "no better than 360p" — so while it is set the controller stands down
        // entirely rather than being allowed to adapt underneath the choice.
        //
        // It also outlives the producer it was set on. A throttled line loses its transport
        // and republishes, often repeatedly, and a pin that lived only on the sender was
        // discarded by every one of those: the class quietly returned to Auto while the menu
        // still showed a tick beside 720p, which is what "the menu does nothing" looked like
        // from the outside. produce() reads this, so the choice survives a reconnect.
        this._rungPin = null
        // Latched once the server is found not to answer setPreferredLayers, so the request
        // is made at most once per session rather than on every consumer and every click.
        this._layersUnsupported = false
        // How many rungs the current producer actually has. A 480p webcam gets two, not
        // three, and setMaxSpatialLayer(2) on a two-layer sender is out of range.
        this._rungCount = VIDEO_LADDER.length
        // rids currently being sent. Kept so the producer score can tell a layer that failed
        // from one that was switched off deliberately.
        this._activeRids = new Set(VIDEO_LADDER.map((rung)=>rung.rid))
        // Previous byte totals and on/off state per layer, for differencing in the ABR log.
        this._layerLast = new Map()
        this._layerActive = new Map()
        // ---- teardown diagnostics ------------------------------------------------------
        // Rolling window of recent network samples. When a transport is torn down, what
        // matters is not the state name but the conditions that led to it, and by then the
        // stats are gone — so the last thirty seconds are kept to be dumped at that moment.
        this._netHistory = []
        // When each transport entered its current connection state, for dwell time.
        this._transportSince = new Map()
        // producer_id -> the still track standing in for a released camera, and the timer
        // repainting its canvas. See swapVideoToStill.
        this.stillTracks = new Map()
        this.camerastream = null
        this.fileName = fileName
        // Network monitor. Byte totals are accumulated from per-tick deltas rather than
        // read as absolutes: a transport is replaced on republish and its counters restart
        // at zero, so an absolute reading would silently drop everything before the swap.
        this._netTimer = null
        this._netLast = new Map()   // transport id -> {bytesSent, bytesReceived, at}
        // Per-RTP-stream last reading, keyed transport|direction|report id, so the audio /
        // video split survives the same transport swaps the candidate-pair totals do.
        this._rtpLast = new Map()
        this._netTotals = {
            bytesSent: 0,
            bytesReceived: 0,
            // The same bytes split by media kind. Camera, screen and whiteboard are all
            // 'video' to WebRTC — only one of them publishes at a time, and the phase label
            // in consumptionPhaseLogger is what says which one it was.
            sentAudioBytes: 0,
            sentVideoBytes: 0,
            recvAudioBytes: 0,
            recvVideoBytes: 0,
        }

        this._useDataChannel = true
        this._boardDataProducer = null
        this._dataConsumers = new Map()
        this._nextDataChannelTestNumber = 0
        animationFrame = false
        this._isOpen = false
        this.eventListeners = new Map()
        // not required for frontend e6
        Object.keys(_EVENTS).forEach(function (evt) {
            this.eventListeners.set(evt, [])
        }.bind(this))

        this.createRoom(room_id).then(async function () {
            await this.join(this.user_id, user, room_id)
            this.initSockets()
            this._isOpen = true
            if (successCallback){
                successCallback()
            }
        }.bind(this))

    }

    // async networkStats() {
    //     await new Promise(resolve => setTimeout(resolve, 2000)); 
    //     this.networkStats()
    // }

    ////////// INIT /////////
    // announce=false releases the overlay capture without saying the camera went off, for the
    // one caller that is replacing it rather than ending it: closeBoard hands the producer a
    // camera of its own first (swapVideoToCamera fires cameraOn with it) and only then lets
    // the board's picture-in-picture go. A cameraOff after that cameraOn describes the end of
    // a capture nobody is looking at any more, and lands on the state the new one just set.
    stopcamera(announce = true) {
        if (this.camerastream) {
          this.endTracks(this.camerastream);
          this.camerastream = null;
          if (announce) this.eventCallback("cameraOff")
        }
    }

    opencamera = async () => {
        let thiz = this;
        navigator.mediaDevices
          .getUserMedia({
            video: true,
            audio: false,
          })
          .then(function (cameraStream) {
            thiz.camerastream = cameraStream;
            thiz.eventCallback("cameraOn", cameraStream);

          })
          .catch((e) => {
            // thiz.callback({
            //   command: "error",
            //   error: "can not turn on the video",
            //   consoleError: e,
            // });
          });
      };

    async createRoom(room_id) {
     let status = await this.socket.request('createRoom', {
            room_id
        }).catch(err => {
            console.log(err)
        })
        if (status==='already exists'){
            this.eventCallback('roomAlreadyExists')
        }
        console.log("80", status)
    }

    async join(user_id, user, room_id, joinCallback) {

        this.socket.request('join', {
            user_id,
            user,
            room_id
        }).then(async function (roomstate) {
            console.log('joined successfully', roomstate)

            try
            {
                const data = await this.socket.request('getRouterRtpCapabilities');
                let device = await this.loadDevice(data)
                this.device = device
                await this.socket.request('resetClosedProducers')
                const teansportCreated = await this.initTransports(device)
                if(!teansportCreated){
                    console.log('Failed to join');
                    
                }
                if (joinCallback){
                    joinCallback();
                }
            }
            catch (error){
                console.log(error);
                
            }
        }.bind(this)).catch(e => {
            console.log(e)
        })
    }

    async loadDevice(routerRtpCapabilities) {
        let device
        try {
            device = new Device();
        } catch (error) {
            if (error.name === 'UnsupportedError') {
                console.error('browser not supported');
            }
            console.error(error)
        }
        await device.load({
            routerRtpCapabilities
        })
        return device

    }

    async initTransports(device) {

        // init producerTransport
        {
            const data = await this.socket.request('createWebRtcTransport', {
                forceTcp: false,
                rtpCapabilities: device.rtpCapabilities,
                sctpCapabilities: this._useDataChannel ?  device.sctpCapabilities :   undefined,
                producing: true,
				consuming: false,
            })
            if (data.error) {
                console.error('createWebRtcTransport failed', data.error);
                return false;
            }

            this.producerTransport = device.createSendTransport(data);

            this.producerTransport.on('connect', async function ({
                dtlsParameters
            }, callback, errback) {
                this.socket.request('connectTransport', {
                        dtlsParameters,
                        transport_id: data.id
                    })
                    .then(callback)
                    .catch(errback)
            }.bind(this));
            const thiz= this
            this.producerTransport.on('produce', async function ({
                kind,
                rtpParameters
            }, callback, errback) {
                try {
                    const processId = kind === 'audio' ? uuidv4() : undefined;
                    if (kind==='audio'){
                        this.processId = processId
                        console.log("244 processIdGenerated", processId)
                        this._netLog(`processIdGenerated: ${processId}`); // TEMP DIAGNOSTIC
                        this.eventCallback('processIdGenerated', processId)
                    }
                    let params = {
                        producerTransportId: this.producerTransport.id,
                        kind,
                        rtpParameters,
                        processId
                    }
                    if (this.fileName){
                        params.fileName = this.fileName
                    }
                    const {
                        producer_id,
                        kindRcv
                    } = await this.socket.request('produce', params);
                    callback({
                        id: producer_id
                    });
                    console.log("kind receive after audio producer created", kindRcv)
                    // kind
                    if(kindRcv==="audio"){
                        thiz.eventCallback('audioAdded')
                    }
                } catch (err) {
                    errback(err);
                }
            }.bind(this))

            this.producerTransport.on('connectionstatechange', function (state) {
                const {previousState, dwellMs} = this._noteTransportState('producer', state);
                switch (state) {
                    case 'connecting':
                        console.log("producer transport state connecting");
                        break;

                    case 'connected':
                        console.log('producer transport state change connected');
                        // Reaching 'connected' from 'disconnected' is ICE having healed on
                        // its own — the case the current teardown never allows to happen.
                        if (previousState === 'disconnected'){
                            this._netLog(`producer transport RECOVERED on its own after ${(dwellMs/1000).toFixed(1)}s — no teardown was needed`);
                        }
                        break;

                    case 'failed':
                        console.log('producer state change failed');
                        this._netLog(`producer transport FAILED after ${(dwellMs/1000).toFixed(1)}s in '${previousState}' — closing (kills mic, camera and screen together)`);
                        this._dumpNetHistory('producer transport failed');
                        if (this.producerTransport){
                            this.producerTransport.close();
                        }
                        break;

                    case "closed":
                        console.log('producer transport state change closed');
                        break;

                    case "disconnected":
                        console.log('producer transport state change disconnected');
                        // The line that answers the question. ICE 'disconnected' is a
                        // silence timeout, not a quality verdict, and the dwell time printed
                        // here is how long the client waited before tearing the class down.
                        this._netLog(`producer transport DISCONNECTED after ${(dwellMs/1000).toFixed(1)}s connected — closing immediately (0s grace)`);
                        this._dumpNetHistory('producer transport disconnected');
                        if (this.producerTransport && this._shouldCloseOnDisconnect()){
                            this.producerTransport.close();
                        }
                        break;

                    default:
                        break;
                }
            }.bind(this));
            this.producerTransport.on('producedata', async (
                {
                    sctpStreamParameters,
                    label,
                    protocol,
                    appData
                },
                callback,
                errback
            ) =>
            {
                // sctpStreamParameters.streamId = parseInt(this.user_id);
                console.log(
                    '"producedata" event: [sctpStreamParameters:%o, appData:%o]',
                    sctpStreamParameters, appData);

                try
                {
                    // eslint-disable-next-line no-shadow
                    const dataProducer = await this.socket.request(
                        'produceData',
                        {
                            user_id: this.user_id,
                            room_id: this.room_id,
                            transportId : this.producerTransport.id,
                            sctpStreamParameters,
                            label,
                            protocol,
                            appData
                        });
                        // console.log('produceData socket request resolved', dataProducer._data.id);
                    callback({ id: dataProducer.id });
                }
                catch (error)
                {
                    errback(error);
                }
            });
        }


        // init consumerTransport
        {
            const data = await this.socket.request('createWebRtcTransport', {
                forceTcp: false,
                rtpCapabilities: device.rtpCapabilities,
                sctpCapabilities: this._useDataChannel ?  device.sctpCapabilities :   undefined,
                producing: false,
				consuming: true,
            });
            if (data.error) {
                console.error(data.error);
                return;
            }

            // only one needed
            this.consumerTransport = device.createRecvTransport(data);

            let stats = await this.consumerTransport.getStats();
            console.log('consumerTransport##', stats)

            this.consumerTransport.on('connect', function ({
                dtlsParameters
            }, callback, errback) {
                this.socket.request('connectTransport', {
                        transport_id: this.consumerTransport.id,
                        dtlsParameters
                    })
                    .then(callback)
                    .catch(errback);
            }.bind(this));

            this.consumerTransport.on('connectionstatechange', async function (state) {
                const {previousState, dwellMs} = this._noteTransportState('consumer', state);
                if (state === 'connected' && previousState === 'disconnected'){
                    this._netLog(`consumer transport RECOVERED on its own after ${(dwellMs/1000).toFixed(1)}s`);
                }
                switch (state) {
                    case 'connecting':
                        console.log("consumer transport state connecting");
                        break;

                    case 'connected':
                        console.log('consumer transport state change connected');
                        //remoteVideo.srcObject = await stream;
                        //await socket.request('resume');
                        break;

                    case 'failed':
                        console.log('consumer transport state change failed');
                        this._netLog(`consumer transport FAILED after ${(dwellMs/1000).toFixed(1)}s in '${previousState}' — closing (stops all incoming video and audio)`);
                        this._dumpNetHistory('consumer transport failed');
                        if (this.consumerTransport){
                            this.consumerTransport.close();
                        }
                        break;
                    case "closed":
                        console.log('consumer transport state change closed');
                        break;

                    case "disconnected":
                        console.log('consumer transport state change disconnected');
                        this._netLog(`consumer transport DISCONNECTED after ${(dwellMs/1000).toFixed(1)}s connected — closing immediately (0s grace)`);
                        this._dumpNetHistory('consumer transport disconnected');
                        if (this.consumerTransport && this._shouldCloseOnDisconnect()){
                            this.consumerTransport.close();
                        }
                        break;

                    default:
                        break;
                }
            }.bind(this));
        }

        this._boardDataProducer = await this.enableDataProducer({ label: 'board'});
        if (this._boardDataProducer){
            console.log('chat board producer created', this._boardDataProducer.id);
        }else{
            // Data channel refused or SCTP not negotiated. The whiteboard's channel is gone
            // for this session, but audio, video and the recording do not depend on it.
            console.log('chat board producer unavailable - continuing without the board data channel');
        }
        EventRegister.emit('broadcast', 'connectionCompleted');
        this.startNetworkMonitor();

        return true;

    }

    // Reads both transports every 2s and reports one combined picture of the connection.
    //
    // Deliberately on the *transports*, not on producers: transport.getStats() carries the
    // candidate-pair, whose byte counters cover every track at once (plus RTCP), and a
    // transport outlives the producer swaps that closeProducer/produce and the camera
    // toggle do constantly. Reading producer.getStats() instead would reset the totals
    // several times a class.
    //
    // Runs for students too. They never publish, so they have no producerTransport at all
    // and the old RTT probe (which lived inside produce()) never ran for them — a student
    // had no network signal of any kind.
    startNetworkMonitor(intervalMs = 2000) {
        this.stopNetworkMonitor();
        this._netTimer = setInterval(() => {
            this.sampleNetwork().catch(() => {});
        }, intervalMs);
    }

    stopNetworkMonitor() {
        if (this._netTimer){
            clearInterval(this._netTimer);
            this._netTimer = null;
        }
    }

    // Byte counters only ever go up within one transport. A lower reading than last time
    // means the transport was replaced (or ICE restarted), so the reading itself is the
    // delta — never a negative, which would eat into the running total.
    _accumulate(transportId, bytesSent, bytesReceived, now) {
        const previous = this._netLast.get(transportId);
        const sentDelta = previous && bytesSent >= previous.bytesSent ? bytesSent - previous.bytesSent : bytesSent;
        const recvDelta = previous && bytesReceived >= previous.bytesReceived ? bytesReceived - previous.bytesReceived : bytesReceived;
        const seconds = previous ? Math.max((now - previous.at) / 1000, 0.001) : 0;
        this._netLast.set(transportId, {bytesSent, bytesReceived, at: now});
        this._netTotals.bytesSent += sentDelta;
        this._netTotals.bytesReceived += recvDelta;
        // No previous sample means no window to divide by — count the bytes, report no rate.
        return seconds ? {upBps: (sentDelta * 8) / seconds, downBps: (recvDelta * 8) / seconds} : {upBps: 0, downBps: 0};
    }

    // Same monotonic-counter rule as _accumulate, per RTP stream. A producer replaced by a
    // camera toggle or a whiteboard swap gets a new ssrc and therefore a new report id, so
    // its counter restarts at zero and the whole reading is the delta.
    _rtpDelta(key, value) {
        const previous = this._rtpLast.get(key);
        const delta = (previous !== undefined && value >= previous) ? value - previous : value;
        this._rtpLast.set(key, value);
        return delta;
    }

    // isSender marks the producer transport. It matters for availableOutgoingBitrate only:
    // that figure is the congestion controller's estimate of what this peer can *send*, so
    // on a receive-only transport it describes the RTCP trickle going back to the server and
    // means nothing as a line speed. Reading both transports the same way had the consumer's
    // number overwriting the producer's, which is the one a publisher wants.
    async _readTransport(transport, out, now, isSender = false) {
        if (!transport || transport.closed) return
        let stats;
        try {
            stats = await transport.getStats();
        } catch (error) {
            return
        }
        let bytesSent = 0;
        let bytesReceived = 0;
        let sawPair = false;
        stats.forEach((report) => {
            if (report.type === 'candidate-pair' && (report.nominated || report.state === 'succeeded')){
                sawPair = true;
                bytesSent += report.bytesSent || 0;
                bytesReceived += report.bytesReceived || 0;
                if (typeof report.currentRoundTripTime === 'number'){
                    out.rtt = report.currentRoundTripTime;
                }
                if (isSender && typeof report.availableOutgoingBitrate === 'number'){
                    out.availableOutgoingBitrate = report.availableOutgoingBitrate;
                }
            }
            // Sent bytes attributed to a kind. The candidate-pair total above stays the
            // answer to "what did this class cost"; this is the same data broken out, so a
            // mic-only stretch can be told apart from a camera one in the log.
            if (report.type === 'outbound-rtp' && !report.isRemote){
                const delta = this._rtpDelta(`${transport.id}|out|${report.id}`, report.bytesSent || 0);
                if (report.kind === 'audio') this._netTotals.sentAudioBytes += delta;
                else this._netTotals.sentVideoBytes += delta;
                // One of these per simulcast layer. A layer that has been switched off keeps
                // filing a report but stops carrying a frame size, so collecting all of them
                // — dormant ones included — is what makes "720p went away" visible rather
                // than merely absent.
                if (report.kind === 'video'){
                    out.layers.push({
                        id: report.id,
                        rid: report.rid ?? 'single',
                        active: report.active !== false,
                        width: report.frameWidth ?? null,
                        height: report.frameHeight ?? null,
                        fps: report.framesPerSecond ?? null,
                        bytesSent: report.bytesSent ?? 0,
                    });
                }
                // Why the encoder is not doing better, straight from libwebrtc: 'bandwidth'
                // is the line, 'cpu' is this machine. Worth keeping apart, because the fix
                // for one is nothing like the fix for the other.
                if (report.kind === 'video' && report.qualityLimitationReason
                    && report.qualityLimitationReason !== 'none'){
                    out.limitedBy = report.qualityLimitationReason;
                }
            }
            // What the far end says it lost of what we sent — the publisher's own view of
            // upstream loss, and the client-side counterpart to producerScore.
            if (report.type === 'remote-inbound-rtp'){
                if (typeof report.fractionLost === 'number'){
                    out.lossSamples.push(report.fractionLost * 100);
                }
                if (typeof report.roundTripTime === 'number' && out.rtt === null){
                    out.rtt = report.roundTripTime;
                }
            }
            // What we failed to receive — the student's view of downstream loss. Cumulative
            // counters, so this is loss over the whole session rather than right now.
            if (report.type === 'inbound-rtp' && !report.isRemote){
                // Same question from the other side: the size being decoded is the size the
                // server chose to send us, so this is where a preferred-layer request either
                // shows up or is revealed to have done nothing.
                if (report.kind === 'video' && report.frameWidth){
                    out.receiving.push({
                        width: report.frameWidth,
                        height: report.frameHeight,
                        fps: report.framesPerSecond ?? null,
                    });
                }
                const delta = this._rtpDelta(`${transport.id}|in|${report.id}`, report.bytesReceived || 0);
                if (report.kind === 'audio') this._netTotals.recvAudioBytes += delta;
                else this._netTotals.recvVideoBytes += delta;
                const lost = report.packetsLost || 0;
                const received = report.packetsReceived || 0;
                if (lost + received > 0){
                    out.lossSamples.push((lost / (lost + received)) * 100);
                }
            }
        });
        if (sawPair){
            const rates = this._accumulate(transport.id, bytesSent, bytesReceived, now);
            out.upBps += rates.upBps;
            out.downBps += rates.downBps;
        }
    }

    async sampleNetwork() {
        const tallest = (frames) => frames.reduce(
            (best, frame) => (!best || frame.height > best.height ? frame : best), null);
        const now = Date.now();
        const out = {upBps: 0, downBps: 0, rtt: null, availableOutgoingBitrate: null, lossSamples: [],
                     layers: [], receiving: [], limitedBy: null};
        await this._readTransport(this.producerTransport, out, now, true);
        await this._readTransport(this.consumerTransport, out, now);
        // Worst layer wins: one badly-lossy stream is what the class actually notices, and
        // averaging it against healthy ones would hide it.
        const lossPct = out.lossSamples.length ? Math.max(...out.lossSamples) : 0;
        const payload = {
            upBps: out.upBps,
            downBps: out.downBps,
            bytesSent: this._netTotals.bytesSent,
            bytesReceived: this._netTotals.bytesReceived,
            sentAudioBytes: this._netTotals.sentAudioBytes,
            sentVideoBytes: this._netTotals.sentVideoBytes,
            recvAudioBytes: this._netTotals.recvAudioBytes,
            recvVideoBytes: this._netTotals.recvVideoBytes,
            rtt: out.rtt,
            lossPct,
            availableOutgoingBitrate: out.availableOutgoingBitrate,
            // Tallest frame wins: with simulcast the smaller layers are also being sent, but
            // the one that decides what a student can see at best is the largest.
            videoSending: tallest(out.layers.filter((layer) => layer.height)),
            videoReceiving: tallest(out.receiving),
            limitedBy: out.limitedBy,
            // What the rung being published costs. The header needs it to tell an encoder
            // that is short of its own budget — a bandwidth fault — from a line that is
            // losing packets, which is a different fault with a different fix. Null while
            // nothing is publishing, and for the single-encoding screen share, which has no
            // rung to be short of.
            rungCostBps: this._videoRung !== null
                ? VIDEO_LADDER[Math.min(this._videoRung, TOP_RUNG)].maxBitrate
                // A locked encoding has no rung, but it does now have a budget, and that is
                // the same fact the header was asking for: what the encoder is currently
                // allowed to spend, so an encoder falling short of it can be told apart from
                // a line dropping packets.
                : (this.producerLabel.has('videoType') ? this._budgetStep().maxBitrate : null),
            // Whether the ladder is being held at 360p waiting for the line to settle. The
            // header reads it to re-measure sooner: getting back up needs a fresh reading
            // clear of the threshold, and on the normal two-minute cadence a class that had
            // already recovered would sit at 360p long after it needed to.
            weakLine: this._weakLocked,
        };
        // Thirty seconds of history, so a teardown can print what preceded it.
        this._netHistory.push({
            at: new Date(now).toLocaleTimeString(),
            up_kbps: Math.round(payload.upBps / 1000),
            down_kbps: Math.round(payload.downBps / 1000),
            rtt_ms: payload.rtt === null ? '—' : Math.round(payload.rtt * 1000),
            loss_pct: Number((payload.lossPct || 0).toFixed(1)),
            bwe_kbps: Number.isFinite(payload.availableOutgoingBitrate)
                ? Math.round(payload.availableOutgoingBitrate / 1000) : '—',
            limited: payload.limitedBy ?? 'none',
        });
        if (this._netHistory.length > 15) this._netHistory.shift();
        this._logVideoLayers(payload, out.layers, now);
        this.eventCallback('networkStats', payload);
        // The controller rides this tick rather than running a timer of its own: it needs
        // exactly the loss, round trip and bandwidth estimate that were just computed, and a
        // second sampler would pay for getStats() twice to learn the same thing.
        this._adaptVideoRung(payload);
        return payload;
    }

    /*
     * Per-layer ABR log — the answer to "did picking 360p actually change anything".
     *
     * The quality button reports the tallest layer still carrying frames, which tells you
     * what is getting through but not what happened to the others. This prints every
     * simulcast layer each tick with its own bitrate, so switching to 360p shows up as r1
     * and r2 going inactive and their kbps falling to zero, rather than as a label quietly
     * changing.
     *
     * On by default on localhost and off everywhere else, because a class in production does
     * not need two console groups a second. Force it either way with window.__ABR_LOG.
     */
    _logVideoLayers(payload, layers, now) {
        const forced = typeof window !== 'undefined' ? window.__ABR_LOG : undefined;
        // A plain list, not a pattern: the dots and brackets in these three need escaping
        // to mean themselves, and a regex that quietly matches more than it should is a poor
        // way to decide whether a production class logs twice a second.
        const onLocalhost = typeof window !== 'undefined'
            && ['localhost', '127.0.0.1', '[::1]'].includes(window.location?.hostname ?? '');
        if (forced === false || (forced !== true && !onLocalhost)) return
        if (!layers.length) return

        const rows = layers
            // r0, r1, r2 in ladder order regardless of what order getStats() returned them.
            .sort((a, b) => String(a.rid).localeCompare(String(b.rid)))
            .map((layer) => {
                // Bitrate has to be differenced: outbound-rtp carries a running byte total,
                // not a rate. A layer seen for the first time has no window to divide by.
                const previous = this._layerLast.get(layer.id);
                this._layerLast.set(layer.id, {bytes: layer.bytesSent, at: now});
                const seconds = previous ? (now - previous.at) / 1000 : 0;
                const kbps = seconds > 0 && layer.bytesSent >= previous.bytes
                    ? Math.round(((layer.bytesSent - previous.bytes) * 8) / seconds / 1000)
                    : 0;
                const ladder = VIDEO_LADDER.find((rung) => rung.rid === layer.rid);
                return {
                    layer: layer.rid,
                    target: ladder ? ladder.label : '—',
                    active: layer.active ? 'yes' : 'NO',
                    actual: layer.height ? `${layer.width}×${layer.height}` : '— (not sending)',
                    fps: Number.isFinite(layer.fps) ? Math.round(layer.fps) : '—',
                    kbps,
                };
            });

        const total = rows.reduce((sum, row) => sum + row.kbps, 0);
        const pin = this._rungPin === null
            ? `auto (at ${VIDEO_LADDER[Math.min(this._videoRung ?? 0, VIDEO_LADDER.length - 1)]?.label ?? '?'})`
            : `pinned ${VIDEO_LADDER[this._rungPin]?.label}`;
        const bwe = Number.isFinite(payload.availableOutgoingBitrate)
            ? `${Math.round(payload.availableOutgoingBitrate / 1000)}kbps` : 'n/a';
        const line = Number.isFinite(this._lineSpeedBps)
            ? `${(this._lineSpeedBps / 1000000).toFixed(1)}Mbps` : 'unmeasured';

        console.groupCollapsed(
            `%c[ABR]%c ${pin} · sending ${total}kbps · bwe ${bwe} · line ${line} · loss ${(payload.lossPct || 0).toFixed(1)}% · limited: ${payload.limitedBy ?? 'none'}`,
            'color:#fff;background:#594099;padding:1px 5px;border-radius:3px;font-weight:700', '');
        console.table(rows);
        console.groupEnd();

        // A layer flipping on or off is the event worth seeing without expanding anything,
        // so it is also printed flat.
        rows.forEach((row) => {
            const was = this._layerActive.get(row.layer);
            const is = row.active === 'yes';
            if (was !== undefined && was !== is){
                console.log(`%c[ABR]%c layer ${row.layer} (${row.target}) ${is ? 'ON' : 'OFF'}`,
                    `color:#fff;background:${is ? '#15803d' : '#b45309'};padding:1px 5px;border-radius:3px;font-weight:700`, '');
            }
            this._layerActive.set(row.layer, is);
        });
    }

    /*
     * Teardown diagnostics. Pure observation — nothing here changes what the client does.
     *
     * The question these answer is "what threshold turned the sharing off", and the honest
     * answer turns out not to be a threshold at all: ICE 'disconnected' is a timeout on
     * packet arrival, roughly five seconds of silence on the selected candidate pair, not a
     * judgement about quality. The log is arranged to make that visible — every teardown
     * prints the conditions immediately before it, so a clean 0% loss / 48ms sample sitting
     * right underneath a teardown line tells its own story.
     */
    _netLog(...args) {
        const forced = typeof window !== 'undefined' ? window.__NET_LOG : undefined;
        const onLocalhost = typeof window !== 'undefined'
            && ['localhost', '127.0.0.1', '[::1]'].includes(window.location?.hostname ?? '');
        if (forced === false || (forced !== true && !onLocalhost)) return
        console.log('%c[NET]%c ' + args[0],
            'color:#fff;background:#b91c1c;padding:1px 5px;border-radius:3px;font-weight:700', '',
            ...args.slice(1));
    }

    // Which call site reached a teardown, without a full stack dump on every line.
    _caller(depth = 3) {
        const lines = (new Error().stack || '').split('\n').slice(2, 2 + depth);
        return lines.map((line) => line.trim().replace(/^at /, '').split(' (')[0]).join(' < ')
    }

    // The conditions leading up to whatever just happened.
    _dumpNetHistory(label) {
        const forced = typeof window !== 'undefined' ? window.__NET_LOG : undefined;
        const onLocalhost = typeof window !== 'undefined'
            && ['localhost', '127.0.0.1', '[::1]'].includes(window.location?.hostname ?? '');
        if (forced === false || (forced !== true && !onLocalhost)) return
        if (!this._netHistory.length){
            console.log('%c[NET]%c ' + label + ': no samples yet',
                'color:#fff;background:#b91c1c;padding:1px 5px;border-radius:3px;font-weight:700', '');
            return
        }
        console.groupCollapsed('%c[NET]%c ' + label + ' — last ' + this._netHistory.length + ' samples before this',
            'color:#fff;background:#b91c1c;padding:1px 5px;border-radius:3px;font-weight:700', '');
        console.table(this._netHistory);
        console.groupEnd();
    }

    // Called from both connectionstatechange handlers. Returns how long the transport sat in
    // the state it is leaving, which is the number that decides whether a teardown was
    // patient or instant.
    _noteTransportState(which, state) {
        const now = Date.now();
        const previous = this._transportSince.get(which);
        const dwellMs = previous ? now - previous.at : 0;
        this._transportSince.set(which, {state, at: now});
        this._netLog(`${which} transport: ${previous?.state ?? 'new'} -> ${state}` +
            (previous ? ` (after ${(dwellMs / 1000).toFixed(1)}s in '${previous.state}')` : ''));
        return {previousState: previous?.state ?? null, dwellMs}
    }

    // The experiment switch. Set window.__NET_NO_CLOSE = true in the console and the client
    // stops closing transports on 'disconnected', so you can watch whether ICE would have
    // come back on its own. Off by default: this is a question, not a fix.
    _shouldCloseOnDisconnect() {
        const skip = typeof window !== 'undefined' && window.__NET_NO_CLOSE === true;
        if (skip) this._netLog('__NET_NO_CLOSE is set — NOT closing, watching for recovery instead');
        return !skip
    }

    // Measured line speed from the speed test in the header. It sets the ceiling the
    // controller may climb to, and seeds the starting rung of the next producer.
    setLineSpeed(bps) {
        this._lineSpeedBps = Number.isFinite(bps) && bps > 0 ? bps : null;
        // A measurement below what 480p costs is the plainest evidence there is, and it
        // should not have to wait for the next 2s stats tick to take effect. The lock goes
        // on here; only the recovery run in _adaptVideoRung ever takes it off.
        if (isWeakLine(this._lineSpeedBps)){
            this._steadyVotes = 0;
            if (!this._weakLocked){
                this._netLog(`measured ${Math.round(this._lineSpeedBps / 1000)}kbps — holding 360p until the line settles`);
            }
            this._weakLocked = true;
        }
    }

    /*
     * The highest rung Auto is allowed to reach right now.
     *
     * Two limits and a lock, lowest wins: the layers this producer actually has, what the
     * measured line can carry, and — overriding both — the weak-line hold at 360p. A pin
     * from the quality menu is not subject to any of them; an explicit choice is the
     * viewer's to make.
     */
    _autoCeiling() {
        if (this._weakLocked) return 0
        return Math.min(rungForSpeed(this._lineSpeedBps), Math.max(this._rungCount - 1, 0))
    }

    /*
     * Quality chosen from the menu. rung is an index into VIDEO_LADDER, or null for Auto.
     *
     * Both halves of the call have to move, and they are different mechanisms. What this
     * peer *sends* is capped locally — setMaxSpatialLayer deactivates the encodings above
     * the choice, so picking 360p genuinely stops encoding 480p and 720p rather than just
     * hiding them. What this peer *receives* is not ours to decide: the layer a consumer
     * gets is selected on the server, so that half is a request over the socket.
     */
    async setVideoQuality(rung) {
        console.log(`%c[ABR]%c request: ${rung === null ? 'Auto' : VIDEO_LADDER[rung]?.label}`,
            'color:#fff;background:#594099;padding:1px 5px;border-radius:3px;font-weight:700', '');
        this._rungPin = rung;
        this._rungDownVotes = 0;
        this._rungUpVotes = 0;

        const producer_id = this.producerLabel.get('videoType');
        const producer = producer_id ? this.producers.get(producer_id) : null;
        if (producer && !producer.closed && this._videoRung !== null){
            // Auto hands the ladder back to the controller from wherever the line can
            // currently sustain — the weak-line hold included — rather than from whatever
            // the viewer happened to pick. An explicit choice is never clamped to either.
            const target = Math.min(rung === null ? this._autoCeiling() : rung,
                                    this._rungCount - 1);
            this._videoRung = target;
            this._rungChangedAt = Date.now();
            this._setActiveRids(target);
            try {
                await producer.setMaxSpatialLayer(target)
                // What the encoder was actually left with, read straight back off the sender
                // rather than assumed. setMaxSpatialLayer works by deactivating the encodings
                // above the target, so this is where a request either took or did not.
                const encodings = producer.rtpSender?.getParameters?.()?.encodings ?? [];
                console.log('[ABR] send encodings now:', encodings.map((encoding, index) => ({
                    rid: encoding.rid ?? `#${index}`,
                    active: encoding.active !== false,
                    maxBitrate: encoding.maxBitrate ?? null,
                    scaleResolutionDownBy: encoding.scaleResolutionDownBy ?? 1,
                })));
            }
            catch (error) { console.log('setMaxSpatialLayer failed', error) }
            this.eventCallback('videoQuality', {
                rung: target,
                label: VIDEO_LADDER[target].label,
                ceiling: VIDEO_LADDER[rungForSpeed(this._lineSpeedBps)].label,
                reason: 'pin',
                pinned: rung !== null,
            });
        }

        await this._applyConsumerLayers();
        return true
    }

    getVideoQualityPin() {
        return this._rungPin
    }

    // setMaxSpatialLayer(n) leaves rungs 0..n sending and switches the rest off, so the
    // active set is just the head of the ladder.
    _setActiveRids(topRung) {
        this._activeRids = new Set(
            VIDEO_LADDER.slice(0, Math.max(topRung, 0) + 1).map((rung)=>rung.rid));
        this._netLog(`active layers now: [${Array.from(this._activeRids).join(', ')}]`);
    }

    /*
     * Ask the server for a specific spatial layer on every video consumer.
     *
     * mediasoup selects layers server-side — consumer.setPreferredLayers lives on the
     * router, not in the browser — so this is a signalling request, and it needs a
     * 'setPreferredLayers' handler on the media server to have any effect. Until that
     * exists the request is rejected and swallowed: the menu still governs what this peer
     * sends, which is the half that is ours to control, and nothing breaks in a live class
     * because the far end has not caught up yet.
     *
     * temporalLayer 2 is the top of L1T3 — the choice is about size, not smoothness, so
     * frame rate is left alone.
     */
    async _applyConsumerLayers(consumer) {
        if (this._layersUnsupported) return
        const targets = consumer ? [consumer] : Array.from(this.consumers.values());
        const spatialLayer = this._rungPin === null ? TOP_RUNG : this._rungPin;
        // TOP_RUNG, not this._rungCount: the layer count that matters for a consumer is the
        // publisher's, which is theirs to decide, not ours.
        const video = targets.filter((each) => each && each.kind === 'video' && !each.closed);
        if (!video.length) return
        try {
            await Promise.all(video.map((each) => this._requestWithTimeout('setPreferredLayers', {
                consumer_id: each.id,
                spatialLayer,
                temporalLayer: 2,
            })))
            console.log(`[ABR] asked server for spatial layer ${spatialLayer} on ${video.length} consumer(s)`);
        } catch (error) {
            // socket.request resolves only when the server acknowledges, and an event with
            // no handler is never acknowledged — so a server that predates this feature
            // leaves the promise pending rather than rejecting. Latching here is what keeps
            // that from accumulating one dead promise per consumer per menu click.
            this._layersUnsupported = true;
            console.log('server did not answer setPreferredLayers; incoming quality stays automatic', error);
        }
    }

    // socket.request has no timeout of its own and is shared by every call in this file, so
    // the bound is applied here rather than there — the other requests are all for events
    // the server is known to handle.
    _requestWithTimeout(type, data, ms = 4000) {
        let timer = null;
        return Promise.race([
            this.socket.request(type, data).finally(() => clearTimeout(timer)),
            new Promise((_, reject) => {
                timer = setTimeout(() => reject(new Error(`${type} timed out`)), ms);
            }),
        ]);
    }

    /*
     * Move the camera up or down the ladder.
     *
     * Deliberately asymmetric. Going down takes two agreeing samples (~4s) because a class
     * that is already breaking up should not wait; going up takes five (~10s) because the
     * evidence that a line has recovered is weaker than the evidence that it has failed —
     * the estimate rises as soon as the encoder stops asking for much, which happens simply
     * because we just lowered the rung. Climbing eagerly on that produces a sawtooth, which
     * looks far worse to a class than sitting one rung low.
     */
    _adaptVideoRung(stats) {
        const producer_id = this.producerLabel.get('videoType');
        const producer = producer_id ? this.producers.get(producer_id) : null;
        if (!producer || producer.closed || producer.paused){
            // Nothing is going out, so there is no strain to read and nobody to warn about
            // it. Start the window over rather than let a paused camera mature into a
            // verdict about a connection it was never being asked to carry.
            this._strainWindow = [];
            this._healthSince = 0;
            return
        }

        // Which controller is driving, decided by the one thing that separates them: whether
        // this producer has layers to step between. `laddered` is the simulcast case. With
        // LOCK_SINGLE_ENCODING — and for every screen share and whiteboard, which stand the
        // ladder down deliberately — it is false, and the budget steps in videoLadder adapt
        // instead.
        //
        // Everything between here and the ladder itself is common to both. The weak-line
        // hold, the "your connection is struggling" prompt and the CPU answer were never
        // about simulcast, and gating the whole method on `_videoRung !== null` had quietly
        // switched all three off for exactly the configuration that ships today: a locked
        // single encoding, adapting only as far as libwebrtc's own estimator would take it,
        // with 'maintain-resolution' having removed that estimator's main lever.
        const laddered = this._videoRung !== null;
        const top = this._rungCount - 1;
        const current = laddered ? Math.min(this._videoRung, top) : 0;
        const budget = this._budgetStep();
        const loss = stats.lossPct || 0;
        const estimate = stats.availableOutgoingBitrate;
        // What is currently being paid for — the rung under simulcast, the step under a
        // locked encoding. Every judgement below compares the estimate against this.
        const currentCost = laddered ? VIDEO_LADDER[current].maxBitrate : budget.maxBitrate;
        // For the prompt, which names what was being sent when the line gave up. A locked
        // encoding has no rung, so the size it is held at is named the same way a measured
        // frame is.
        const currentLabel = laddered
            ? VIDEO_LADDER[current].label
            : labelForHeight(singleEncodingTarget(this._sendingScreen).height);
        // swapVideoToStill leaves the producer sending a 1fps canvas. It costs almost
        // nothing, so neither its frame size nor the bandwidth estimate says anything about
        // what the line would do with a real picture.
        const onStill = this.stillTracks.has(producer_id);

        // Has the encoder already shrunk the picture below the rung it is meant to be at?
        // That is the third way to learn this rung is not being carried, and usually the
        // first to arrive — libwebrtc drops the size the moment it runs short, which is
        // where 540p and 240p come from. Treating it as strain steps the ladder down a rung,
        // so the frame lands back on a size the ladder has instead of sitting between two.
        //
        // Measured against what this camera can actually supply, and ignored for a few
        // seconds after a move: a 240p webcam on the 360p rung is giving everything it has,
        // and a layer that has just been switched on starts small and grows into its size.
        // The size this producer is meant to be sending: a rung under simulcast, and under a
        // locked encoding whatever singleEncodingTarget holds the camera or the screen at.
        const targetHeight = laddered
            ? VIDEO_LADDER[current].height
            : singleEncodingTarget(this._sendingScreen).height;
        const nominal = Number.isFinite(this._captureHeight) && this._captureHeight > 0
            ? Math.min(targetHeight, this._captureHeight)
            : targetHeight;
        const sending = stats.videoSending?.height ?? null;
        // A budget change is as much a disturbance as a rung change: the encoder takes a
        // moment to settle into a new bitrate, and reading its transient as evidence about
        // the line is what turns a controller into an oscillator.
        const settling = Date.now() - Math.max(this._rungChangedAt, this._budgetChangedAt) < RUNG_SETTLE_MS;
        const shrunk = !onStill && !settling && Number.isFinite(sending) && sending > 0
            && sending < nominal * DOWNSCALE_TOLERANCE;

        // Why the encoder is not doing better, in libwebrtc's own words. 'cpu' and
        // 'bandwidth' are different faults with opposite remedies, and the ladder used to
        // answer both by stepping down — which fixes one and makes the other worse while
        // telling the teacher their network is bad.
        const cpuTight = stats.limitedBy === 'cpu';

        // Down on any of the three: loss means the picture is already breaking, an estimate
        // below what this rung costs means it is about to, and a shrunken frame means the
        // encoder has decided it already.
        //
        // The shrunken frame counts only when the machine is not the stated reason. Under
        // CPU pressure libwebrtc shrinks the picture too, and reading that as the line
        // failing is what dragged a class on a perfectly good connection down the ladder a
        // rung at a time.
        //
        // Is the encoder actually pushing against what it is allowed? availableOutgoingBitrate
        // is demand-driven — congestion control grows it only while something presses on it,
        // and it settles just above whatever is being sent once nothing does. A whiteboard
        // is near-static line art costing a couple of hundred kbps, so on a 30 Mbps line the
        // estimate plateaus in the high hundreds and stays there, permanently "below" the
        // 1.5 Mbps a moving 720p picture would cost.
        //
        // Without this gate that plateau reads as a failing line, and the controller answers
        // by taking a perfectly healthy board down a step every four seconds until it is
        // publishing at 5fps. The estimate only means something next to what we are trying
        // to spend.
        const pressing = Number.isFinite(stats.upBps) && stats.upBps > 0
            && Number.isFinite(estimate) && estimate > 0
            && stats.upBps >= estimate * 0.75;

        const bandwidthTight = loss >= 5
            || (pressing && estimate < currentCost * 0.8)
            || (shrunk && !cpuTight);

        // ---- the weak-line hold -------------------------------------------------------
        // Two ways in: the measurement says the line cannot carry the second rung, or the
        // bottom rung is itself failing, which says the same thing about a line no test has
        // caught up with yet. Either way 360p is the ceiling until it is earned back.
        if (isWeakLine(this._lineSpeedBps) || (bandwidthTight && current === 0 && !onStill)){
            if (!this._weakLocked){
                const line = Number.isFinite(this._lineSpeedBps)
                    ? `${Math.round(this._lineSpeedBps / 1000)}kbps` : 'unmeasured';
                this._netLog(`weak line (${line}, loss ${loss.toFixed(1)}%) — holding 360p until it settles`);
            }
            this._weakLocked = true;
        }
        // Stable is more than a momentary lull: nothing straining, loss under control, and a
        // measurement clear of the threshold by RECOVERY_MARGIN — all held for
        // RECOVERY_SAMPLES consecutive samples. Unlocking on one good tick is exactly what
        // makes a borderline connection oscillate.
        const lineClear = !Number.isFinite(this._lineSpeedBps)
            || this._lineSpeedBps >= WEAK_LINE_BPS * RECOVERY_MARGIN;
        this._steadyVotes = (!bandwidthTight && loss < 2 && lineClear) ? this._steadyVotes + 1 : 0;
        if (this._weakLocked && this._steadyVotes >= RECOVERY_SAMPLES){
            this._weakLocked = false;
            this._netLog(`line stable for ${RECOVERY_SAMPLES} samples — ladder unlocked`);
        }

        // ---- is this worth interrupting the teacher about -----------------------------
        // Most of the last half minute spent in trouble, while a real picture was going out.
        // Deliberately not "the line is weak": a weak line quietly carrying 360p with no loss
        // is the ladder doing its job, and nagging about it would put this dialog in front of
        // every teacher on a modest connection. What earns the interruption is a class that
        // keeps breaking up.
        const now = Date.now();
        if (!this._healthSince) this._healthSince = now;
        // A still frame is evidence of nothing — it costs almost nothing to send, so it
        // neither strains a line nor proves one healthy. Leaving those samples out of the
        // window entirely stops a camera-off stretch from washing it clean.
        // Bandwidth only. A machine that cannot keep up is a real problem and gets its own
        // answer below, but it is not what the "your connection is struggling" prompt is
        // for, and counting it here would put that prompt in front of the wrong teacher.
        if (!onStill) this._strainWindow.push({at: now, strained: bandwidthTight});
        this._strainWindow = this._strainWindow.filter((sample) => now - sample.at <= WEAK_PROMPT_MS);

        const samples = this._strainWindow.length;
        const bad = this._strainWindow.filter((sample) => sample.strained).length;
        // `bandwidthTight` first, so the question is only ever put while the trouble is
        // still happening. Without it a bad stretch that ends cleanly still trips the ratio
        // on the way out, and the dialog arrives seconds after the picture came good —
        // asking a teacher to turn off a camera that is working again.
        const troubled = bandwidthTight
            && samples >= WEAK_PROMPT_MIN_SAMPLES
            && now - this._healthSince >= WEAK_PROMPT_MS
            && bad / samples >= WEAK_PROMPT_RATIO;

        if (troubled && !onStill && now - this._weakPromptedAt >= WEAK_PROMPT_COOLDOWN_MS){
            this._weakPromptedAt = now;
            this._netLog(`${bad}/${samples} of the last ${Math.round(WEAK_PROMPT_MS / 1000)}s strained — suggesting camera off`);
            this.eventCallback('networkWeak', {
                lossPct: loss,
                lineSpeedBps: this._lineSpeedBps,
                label: currentLabel,
                limitedBy: stats.limitedBy ?? null,
                strainedPct: Math.round((bad / samples) * 100),
            });
        }

        // ---- the machine ---------------------------------------------------------------
        // Only when the line is not also tight: if both are true the line is the fault worth
        // answering, because a rung the uplink cannot carry does not become carriable by
        // sending it more smoothly. Runs before the pin check on purpose — a teacher who has
        // pinned 720p has pinned a resolution, not a frame rate, and a machine that cannot
        // encode it still needs the relief.
        this._cpuVotes = (cpuTight && !bandwidthTight) ? this._cpuVotes + 1 : 0;
        this._cpuClearVotes = cpuTight ? 0 : this._cpuClearVotes + 1;
        if (!this._cpuEased && this._cpuVotes >= CPU_EASE_VOTES){
            this._easeCpu(producer, true);
        } else if (this._cpuEased && this._cpuClearVotes >= CPU_RESTORE_VOTES){
            this._easeCpu(producer, false);
        }

        // ---- the budget, where there is no ladder -------------------------------------
        // A locked encoding has no layer to step between, so what adapts is what it may
        // spend and how many frames it spends it on. The resolution is untouched by design:
        // a geometry change at the ingest is the whole reason simulcast is switched off
        // here, and a controller that reintroduced one would reintroduce the fault with it.
        //
        // Deliberately not gated on the quality pin, which the ladder below is. A pin is a
        // choice of resolution and this changes none, so standing down under it would leave
        // a pinned class with no adaptation at all — the state this controller exists to
        // end.
        if (!laddered){
            this._adaptVideoBudget(producer, {bandwidthTight, cpuTight, loss, estimate, onStill, settling, pressing});
            return
        }

        // ---- the ladder itself --------------------------------------------------------
        // A pinned quality is a decision, not a hint. Adapting under it would make the menu
        // lie: the label would read 720p while the class quietly sent 360p. The health
        // tracking above still runs — a pinned class has a connection worth watching, and is
        // if anything likelier to be in trouble.
        if (this._rungPin !== null) return

        const ceiling = this._autoCeiling();
        // Up only when the line is clean and the estimate covers the next rung with room to
        // spare — at exactly the next rung's cost we would be stepping up into congestion.
        //
        // And never while the camera is off, or while the frame is still short of the size
        // it is meant to be. swapVideoToStill leaves the producer sending a 1fps canvas,
        // which costs almost nothing, so the estimate climbs to the top of the ladder on
        // evidence that says only "we are not sending anything". The camera would then come
        // back at 720p on a line that never carried it, and the class would watch it break
        // up and step back down. Falling is still allowed: a still frame that cannot get
        // through means real trouble.
        const nextCost = current < top ? VIDEO_LADDER[current + 1].maxBitrate : Infinity;
        //
        // And never while the machine is the bottleneck. A bigger rung is more to encode,
        // which is precisely what it cannot keep up with — the estimate may well have the
        // room, and taking it would make the only real problem worse.
        const roomy = !onStill
            && !shrunk
            && !cpuTight
            && loss < 2
            && Number.isFinite(estimate)
            && estimate > nextCost * 1.3;

        this._rungDownVotes = bandwidthTight ? this._rungDownVotes + 1 : 0;
        this._rungUpVotes = roomy ? this._rungUpVotes + 1 : 0;

        let target = current;
        if (this._rungDownVotes >= 2 && current > 0) target = current - 1;
        else if (this._rungUpVotes >= 5 && current < top) target = current + 1;
        // The ceiling is also how the weak-line hold is enforced, and why it drops to the
        // bottom in one move rather than a rung every four seconds: a line already measured
        // too weak for 480p gains nothing from proving it on the way down.
        target = Math.min(target, ceiling);

        if (target === current) return
        this._rungDownVotes = 0;
        this._rungUpVotes = 0;
        this._videoRung = target;
        this._rungChangedAt = Date.now();
        this._setActiveRids(target);
        producer.setMaxSpatialLayer(target).catch((error) => {
            console.log('setMaxSpatialLayer failed', error);
        });
        this.eventCallback('videoQuality', {
            rung: target,
            label: VIDEO_LADDER[target].label,
            ceiling: VIDEO_LADDER[ceiling].label,
            reason: target < current ? 'down' : 'up',
        });
    }

    /*
     * The step currently being spent, clamped to whichever table this producer is on.
     *
     * Read rather than stored, because a swap between camera and screen changes the table
     * and not the index: a screen share dropped to step 3 and swapped back to the camera
     * would otherwise carry a screen's 450kbps onto a 360p face, which is the same class of
     * mistake _rescaleEncodings exists to prevent for the resolution.
     */
    _budgetStep() {
        const steps = bitrateStepsFor(this._sendingScreen);
        return steps[Math.min(Math.max(this._budgetIndex, 0), steps.length - 1)]
    }

    /*
     * Step the locked encoding's budget down under strain, and back up once the line has
     * shown it means it.
     *
     * The inputs are the ones the ladder already computes — the two controllers disagree
     * about what to do with strain, not about what strain is. Only the response differs:
     * this one spends bitrate and frame rate where the ladder spends pixels.
     *
     * A still frame is excluded for the same reason it is everywhere else here: a 1fps
     * canvas costs almost nothing, so it neither strains a line nor proves one healthy, and
     * a budget climbed on that evidence would meet the camera coming back.
     */
    _adaptVideoBudget(producer, {bandwidthTight, cpuTight, loss, estimate, onStill, settling, pressing}) {
        const steps = bitrateStepsFor(this._sendingScreen);
        const floor = steps.length - 1;
        const current = Math.min(Math.max(this._budgetIndex, 0), floor);
        if (onStill || settling) return

        // Climbing is measured against what the step above costs, not what this one does.
        // Arriving with exactly the next step's budget is arriving in the congestion that
        // budget will cause; a third again is the margin the ladder uses for the same call.
        //
        // And never while the machine is the bottleneck: a bigger budget is more to encode,
        // which is precisely what it is failing to keep up with.
        //
        // A stream that is not pressing against its allowance climbs regardless of the
        // estimate, and has to. Once the budget is no longer the thing holding the encoder
        // back, the estimate stops growing — it is demand-driven — so an unpressed stream
        // that waited for the estimate to cover the step above would wait forever, and a
        // whiteboard that stepped down during one genuinely bad minute would spend the rest
        // of the class at 8fps for no reason. Raising a cap nothing is reaching costs
        // nothing; if the content then starts to need it, the next samples press and the
        // step comes back down.
        const nextUp = current > 0 ? steps[current - 1].maxBitrate : null;
        const roomy = nextUp !== null && !cpuTight && loss < 2
            && (!pressing || (Number.isFinite(estimate) && estimate > nextUp * 1.3));

        this._budgetDownVotes = bandwidthTight ? this._budgetDownVotes + 1 : 0;
        this._budgetUpVotes = roomy ? this._budgetUpVotes + 1 : 0;

        // Index 0 is the most expensive step, so down the list is down in quality.
        let target = current;
        if (this._budgetDownVotes >= BUDGET_DOWN_VOTES && current < floor) target = current + 1;
        else if (this._budgetUpVotes >= BUDGET_UP_VOTES && current > 0) target = current - 1;
        if (target === current) return

        this._budgetDownVotes = 0;
        this._budgetUpVotes = 0;
        this._budgetIndex = target;
        this._budgetChangedAt = Date.now();
        const step = steps[target];
        const fps = step.maxFramerate ? ` @ ${step.maxFramerate}fps` : ' @ uncapped fps';
        this._netLog(`send budget ${target > current ? 'down' : 'up'} to ` +
            `${Math.round(step.maxBitrate / 1000)}kbps${fps} — resolution unchanged`);
        this._applyVideoBudget(producer);
    }

    /*
     * Write the current step onto the sender: a bitrate, and the frame rate that bitrate can
     * honestly carry at this size.
     *
     * scaleResolutionDownBy is conspicuously not in the list, and that is the point. The
     * transcoder rebuilds its scale graph on an input geometry change and nothing else, so
     * a controller confined to these two fields can run as often as it likes without ever
     * costing the class the thing the ladder cost it.
     *
     * The frame rate written is the lower of what the step asks for and what a struggling
     * machine has been eased to. Both reasons to cap it are real and independent — a laptop
     * that cannot encode 30fps does not start being able to when the line recovers — and
     * they write the same field, so they are reconciled here rather than racing.
     */
    async _applyVideoBudget(producer) {
        const sender = producer?.rtpSender;
        if (!sender?.getParameters) return
        try {
            const parameters = sender.getParameters();
            const encodings = parameters?.encodings;
            if (!encodings?.length) return
            const step = this._budgetStep();
            const caps = [step.maxFramerate, this._cpuEased ? CPU_EASED_FPS : null]
                .filter((value) => Number.isFinite(value) && value > 0);
            const fps = caps.length ? Math.min(...caps) : null;
            encodings.forEach((encoding) => {
                encoding.maxBitrate = step.maxBitrate;
                // Absent, not generous: a track captured at 25fps should not be told it may
                // do 30, and step 0 is meant to behave exactly as it did before this
                // controller existed.
                if (fps) encoding.maxFramerate = fps;
                else delete encoding.maxFramerate;
            });
            await sender.setParameters(parameters);
        } catch (error) {
            // The same failure mode as everything else on this path: the class is left
            // exactly as it was, which is worse than the fix and no worse than before it.
            console.log('send budget not applied', error);
        }
    }

    /*
     * Back to nominal, for a producer that has just started carrying something else.
     *
     * The tables differ between camera and screen, and a budget earned by watching one
     * track behave says nothing about the track that has replaced it.
     *
     * Only the bookkeeping. What reaches the sender is _rescaleEncodings, which writes
     * singleEncodingTarget's bitrate — the head of the step table by construction, for both
     * tables — and the frame-rate clear beside it in _tuneVideoSender.
     */
    _resetVideoBudget() {
        this._budgetIndex = 0;
        this._budgetDownVotes = 0;
        this._budgetUpVotes = 0;
        this._budgetChangedAt = Date.now();
    }

    /*
     * Hold the frame rate down, or give it back.
     *
     * The whole of the CPU answer. maxFramerate is set on every encoding rather than on the
     * top one alone: the cost being saved is the encode, and each simulcast layer is encoded
     * separately, so capping only the largest leaves two thirds of the work in place.
     *
     * Deliberately not setMaxSpatialLayer. Switching the top layer off would drop what every
     * student sees to 480p to solve a problem on one teacher's laptop, and it is the pixels
     * — the code on the shared screen, the diagram on the whiteboard — that a class cannot
     * afford to lose. Frames are the cheaper thing to give up.
     *
     * Restoring deletes the cap rather than setting it back to 30: the constraint on the
     * encoding is meant to be absent, not merely generous, and a track captured at 25fps
     * should not be told it may do 30.
     */
    async _easeCpu(producer, on) {
        const sender = producer?.rtpSender;
        if (!sender?.getParameters) return
        // A locked encoding already has a frame-rate cap of its own, set by the budget step.
        // Writing this one straight onto the encoding would overwrite that cap on the way on
        // and delete it on the way off, so a machine recovering from CPU pressure would hand
        // a 120kbps step permission to send 30fps. _applyVideoBudget takes the lower of the
        // two instead.
        if (this._videoRung === null){
            this._cpuEased = on;
            this._cpuVotes = 0;
            this._cpuClearVotes = 0;
            await this._applyVideoBudget(producer);
            this._netLog(on
                ? `cpu-limited — holding ${CPU_EASED_FPS}fps, resolution unchanged`
                : 'cpu pressure cleared — frame rate back to the budget step');
            return
        }
        try {
            const parameters = sender.getParameters();
            if (!parameters?.encodings?.length) return
            parameters.encodings.forEach((encoding) => {
                if (on) encoding.maxFramerate = CPU_EASED_FPS;
                else delete encoding.maxFramerate;
            });
            await sender.setParameters(parameters);
            this._cpuEased = on;
            this._cpuVotes = 0;
            this._cpuClearVotes = 0;
            this._netLog(on
                ? `cpu-limited — holding ${CPU_EASED_FPS}fps, resolution unchanged`
                : 'cpu pressure cleared — frame rate restored');
        } catch (error) {
            // An older sender that will not take the property leaves the class exactly as it
            // was, which is the right failure: worse than the fix, no worse than before it.
            console.log('cpu frame rate not applied', error);
        }
    }

    // A fresh producer is built from encodings that carry no frame-rate cap, so whatever was
    // being held down belonged to the sender that has just gone. Said here rather than left
    // to the restore votes, which would spend four samples discovering it.
    _resetCpuEase() {
        this._cpuEased = false;
        this._cpuVotes = 0;
        this._cpuClearVotes = 0;
    }

    getNetworkTotals() {
        return {...this._netTotals};
    }

    async enableDataProducer({ label })
	{
		// console.log('enableChatDataProducer()');
            
		if (!this._useDataChannel)
            return;
		try
		{
			const dataProducer = await this.producerTransport.produceData(
				{
					ordered        : false,
					maxRetransmits : 1,
					label          : label,
					priority       : 'medium',
                    appData        : { info: label+'-DataProducer' }
                });
                
            // console.log('data producer created', dataProducer);
			dataProducer.on('transportclose', () =>
			{
				console.log('chat DataProducer "transportclose" event');
				// dataProducer = null;
			});

			dataProducer.on('open', () =>
			{
				console.log('chat DataProducer "open" event');
			});

			dataProducer.on('close', () =>
			{
				console.log('chat DataProducer "close" event');
				// dataProducer = null;
			});

			dataProducer.on('error', (error) =>
			{
				console.log('chat DataProducer "error" event:%o', error);
			});

			dataProducer.on('bufferedamountlow', () =>
			{
				console.log('chat DataProducer "bufferedamountlow" event');
            });
            
            return dataProducer;
		}
		catch (error)
		{
			console.log('enableChatDataProducer() | failed:%o', error);
			// throw error;
		}
    }

    async sendBoardMessage(text)
	{
		console.log('sendBoardMessage() [text:"%s]', text);

		if (!this._boardDataProducer)
		{
			console.log(
				{
					type : 'error',
					text : 'No board DataProducer'
				});
			return;
		}

		try
		{
			this._boardDataProducer.send(text, 51);
		}
		catch (error)
		{
			console.log('board _boardDataProducer.send() failed:%o', error);
		}
    }


    async _handleNewDataConsumer(data){
        const {
            peerId, // NOTE: Null if bot.
            dataProducerId,
            id,
            sctpStreamParameters,
            label,
            protocol,
            appData
        } = data;
        console.log("handle new data consumer 399", data)
        try
		{
            const dataConsumer = await this.consumerTransport.consumeData(
                {
                    id,
                    dataProducerId,
                    sctpStreamParameters,
                    label,
                    protocol,
                    // appData : { ...appData, peerId } // Trick.
                });	
            this._dataConsumers.set(dataConsumer.id, dataConsumer);
            console.log('dataConsumer created in client', dataConsumer.id);
            
            dataConsumer.on('transportclose', () =>
            {
                this._dataConsumers.delete(dataConsumer.id);
            });

            dataConsumer.on('open', () =>
            {
                console.log('DataConsumer "open" event');
            });

            dataConsumer.on('close', () =>
            {
                console.log('DataConsumer "close" event');
                this._dataConsumers.delete(dataConsumer.id);
            });

            dataConsumer.on('error', (error) =>
            {
                console.log('DataConsumer "error" event:%o', error);
            });
            dataConsumer.on('message', (message) =>
            {
                console.log(
                    'DataConsumer "message" event [streamId:%d]',
                    dataConsumer.sctpStreamParameters.streamId);

                if (message instanceof ArrayBuffer)
                {
                    const view = new DataView(message);
                    const number = view.getUint32();
                    if (number == Math.pow(2, 32) - 1)
                    {
                        console.log('dataChannelTest finished!');
                        this._nextDataChannelTestNumber = 0;
                        return;
                    }
                    if (number > this._nextDataChannelTestNumber)
                    {
                        console.log(
                            'dataChannelTest: %s packets missing',
                            number - this._nextDataChannelTestNumber);
                    }
                    this._nextDataChannelTestNumber = number + 1;
                    return;
                }
                else if (typeof message !== 'string')
                {
                    logger.warn('ignoring DataConsumer "message" (not a string)');
                    return;
                }	
                switch (dataConsumer.label)
                {
                    case 'chat':
                    {
                        console.log(
                            {
                                title   : `says:`,
                                text    : message,
                                timeout : 5000
                            });

                        break;
                    }

                    case 'board':
                    {
                        this.eventCallback('headlessCommunication', message);
                        console.log('dataChannel board data', message);
                        break;
                    }
                    case 'bot':
                        {
                            console.log('bot data');
                            break;
                        }
                }
                
			});
            console.log("dataconsumer created successfully 492")
        }
        catch (error)
        {
            console.log('"newDataConsumer"  failed to create :%o', error);
            return;
        }
    }

    initSockets() {
        this.socket.on('consumerClosed', function ({
            consumer_id
        }) {
            console.log('closing consumer:', consumer_id)
            this.removeConsumer(consumer_id)
        }.bind(this))

        this.socket.on('newProducers', async function (data) {
            console.log('new producers', data)
            for (let {
                    producer_id, user_id
                } of data) {
                // await this.consume(producer_id, user_id)
            }
        }.bind(this))

        this.socket.on('newDataConsumer', async function (data) {
            console.log('newDataConsumer', data)
            this._handleNewDataConsumer(data);
        }.bind(this))
        
        this.socket.on('dataConsumerClosed', function (data) {
            console.log('dataConsumerClosed', data);
            const { dataConsumerId } = data;
			const dataConsumer = this._dataConsumers.get(dataConsumerId);

			if (!dataConsumer)
				return;

			dataConsumer.close();
			this._dataConsumers.delete(dataConsumerId);
            
        }.bind(this))

        this.socket.on(
            'boardDataRcv',
            function (data) {
              console.log('boardDataRcv', data);
            //   this.userList = data;
            //   EventRegister.emit('userList', this.userList);
            }.bind(this),
          );


        this.socket.on(
        'openBoardRcv',
        function (data) {
            console.log('chatRcv', data);
        //   EventRegister.emit('userList', this.userList);
        }.bind(this),
        );
        this.socket.on(
        'closeBoardRcv',
        function (data) {
            console.log('chatRcv', data);
        //   EventRegister.emit('userList', this.userList);
        }.bind(this),
        );

        this.socket.on('roomDetails', function (data) {
            console.log('roomDetails', data);
            
        }.bind(this))

        this.socket.on('userName', function (data) {
            console.log('userName', data);
            
        }.bind(this))


  
      this.socket.on(
        'userList',
        function (data) {
          console.log('userList', data);
        //   this.userList = data;
        //   EventRegister.emit('userList', this.userList);
        }.bind(this),
      );
  
      this.socket.on(
        'userJoined',
        function (data) {
            console.log('newUserJoined', data);
            
        //   this.userList.push(data);
        //   EventRegister.emit('userList', this.userList);
        }.bind(this),
      );

      this.socket.on(
        'userRejoined',
        function (data) {
            console.log('userRejoined', data);
            
        //   this.userList.push(data);
        //   EventRegister.emit('userList', this.userList);
        }.bind(this),
      );
      
      this.socket.on(
        'userLeft',
        function (data) {
            console.log('userLeft', data);
            
        //   this.userList = this.userList.filter(
        //     (user) => user.user_socket_id !== data.user_socket_id,
        //   );
        //   EventRegister.emit('userList', this.userList);
        }.bind(this),
      );
  
      this.socket.on(
        'eventNotification',
        function (data) {
          console.log('got eventNotification ', data);
          this.eventCallback('headlessCommunication', data)
        //   var x = document.getElementById("snackbar");
        //     x.className = "show";
        //     x.innerHTML = `${data.user} ${data.action} ${data.option}`
        //     setTimeout(function(){ x.className = x.className.replace("show", ""); }, 3000);
        //   this.eventNotification.push(data);
        //   EventRegister.emit('eventNotification', this.eventNotification);
        }.bind(this),
      );
  
      this.socket.on(
        'activeSpeaker',
        function (data) {
        //   console.log('activeSpeaker', data);
        //   this.activeSpeaker = this.remoteMedia.filter(
        //     (media) => media.audio && media.audio.producer_id === data.producerId,
        //   );
        //   this.activeSpeaker =
        //     this.activeSpeaker.length === 0
        //       ? {userName: 'Me'}
        //       : this.activeSpeaker[0];
        //   console.log('filter ative spker', this.activeSpeaker);
        //   EventRegister.emit('activeSpeaker', this.activeSpeaker);
        }.bind(this),
      );
  
      this.socket.on(
        'pauseProducerRemoteRcv',
        function (producer_id) {
          console.log('pausing producer as requested', producer_id);
          if (this.producers.has(producer_id)) {
            this.producers.get(producer_id).pause();
          } else {
            console.log('not my producer to pause');
          }
        }.bind(this),
      );
  
      this.socket.on(
        'resumeProducerRemoteRcv',
        function (producer_id) {
          console.log('resuming producer as requested', producer_id);
          if (this.producers.has(producer_id)) {
            this.producers.get(producer_id).resume();
          } else {
            console.log('not my producer to pause');
          }
        }.bind(this),
      );


      this.socket.on('disconnect', function () {
        this.offline = true;
        // this.exit(true)
        console.log('socket disconnected');
        
        // this.socket.connect();
      }.bind(this))

      this.socket.on('connect_error', (error) => {
        console.log('connect_error', error);
        
      });
      this.socket.on('connect_timeout', (timeout) => {
        console.log('connect_timeout', timeout);

      });
      this.socket.on('error', (error) => {
        console.log('error', error);

      });
      this.socket.on('reconnect', (attemptNumber) => {
        console.log('reconnect', attemptNumber);
        
      });
      this.socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('reconnect_attempt', attemptNumber);

      });
      this.socket.on('reconnecting', (attemptNumber) => {
        console.log('reconnecting', attemptNumber);

      });
      this.socket.on('reconnect_error', (error) => {
        console.log('reconnect_error', error);

      });
      this.socket.on('reconnect_failed', () => {
        console.log('reconnect_failed');
        
      });
      this.socket.on('numberOfConsumer', (noConsumer) => {
        console.log('numberOfConsumer', noConsumer);
        // The expected-count check ('!= 6') is REMOVED pending a backend definition of what
        // numOfConsumer actually counts and what a healthy value is for a room of a given
        // size. Six is not a number a small room ever reports, so a two-person web-to-web
        // class failed the check on every message and reconnected the producers on a loop.
        // Each reconnect mints a fresh processId, and that is what starved the web viewer's
        // fileCreating gate — while a mobile viewer, which does not read that gate, played
        // the same HLS playlist fine throughout. That asymmetry is the whole bug report.
        //
        // The value is still observed. Nothing is decided from it until the backend says
        // what a wrong count would mean; this is the only place 'consumererror' was ever
        // emitted, and its handler in index.js is left in place for a real one.
        this._netLog(`numberOfConsumer: got ${noConsumer?.numOfConsumer} (observed only, no reconnect)`);
      });
      this.socket.on('monitoringMessage', (data) => {
          // TEMP DIAGNOSTIC (safe to remove)
          this._netLog(`monitoringMessage: ${data?.message} (processId=${data?.processId})`);
          if (data.message==='file not creating'){
              this.eventCallback('fileNotCreating', data.processId)
          }else if(data.message==='file is creating'){
            this.eventCallback('fileCreating', data.processId)
          }else if(data.message==='file end creation'){
            this.eventCallback('fileEndCreating', data.processId)
          }
        console.log('monitoringMessage', data);
      });

      // mediasoup's per-producer transmission score, relayed by the server: 0-10, derived
      // from packet loss on that RTP stream. One value per user per kind ('audio'/'video'),
      // for every producer in the room — not just our own — so it is the only signal a
      // student has about how well the teacher is actually getting through.
      // Arrow function on purpose: the old handler was a `function`, so `this` was the
      // socket and it could not have reached eventCallback even if it had tried.
      this.socket.on('producerScore', (data) => {
        const {userId, kind} = data || {};
        if (userId===undefined || userId===null) return
        // mediasoup reports score as [{ssrc, rid, score}] per spatial layer; the server
        // flattens it to a number for most rooms but not all.
        //
        // Taking the worst layer was wrong once simulcast layers could be switched off on
        // purpose. Pinning 360p deactivates r1 and r2, those streams stop, mediasoup scores
        // them 0, the minimum picks up that 0 and connectionQuality reports 'poor' — so
        // choosing a LOWER quality made the header claim a WORSE network, on a line doing
        // 140Mbps. Two rules fix it, in order:
        const raw = data?.score;
        let score;
        if (Array.isArray(raw)){
            const entries = raw
                .map((entry)=>({rid: entry?.rid ?? null, value: Number(entry?.score ?? 10)}))
                .filter((entry)=>Number.isFinite(entry.value));
            // 1. Our own producer: we know exactly which rids we deactivated, so drop them.
            const mine = `${userId}`===`${this.user_id}`;
            const known = mine && kind==='video' && this._activeRids.size
                ? entries.filter((entry)=>entry.rid===null || this._activeRids.has(entry.rid))
                : entries;
            // 2. Anyone else's: we cannot know their layer choices, but a zero sitting beside
            //    a healthy layer means "not sending", while a producer in real trouble has
            //    every layer low. So zeros are ignored only when something else is fine.
            const alive = known.filter((entry)=>entry.value > 0);
            const use = alive.length ? alive : known;
            score = use.reduce((low, entry)=>Math.min(low, entry.value), 10);
        } else {
            score = Number(raw);
        }
        if (!Number.isFinite(score)) return
        this.eventCallback('producerScore', {userId: `${userId}`, kind, score});
      });
      this.socket.on('ping', () => {
        // console.log('ping');

      });
      this.socket.on('pong', (ms) => {
        // console.log('pong latency', ms);
      });

      this.socket.on('rtmpRecordStarted',  (link) => {
        console.log('rtmp record Started  ', link);
        this.eventCallback('rtmpRecordStarted')
      })

      this.socket.on('rtmpRecordStopped', () =>  {
        console.log('rtmp record stopped ');
        this.eventCallback('rtmpRecordStopped')
      })

      this.socket.on('connect', function ()  {
        this.offline = false;
        console.log('socket connected', this.user_id, this.room_id);
        this.socket.request('join', { 
            user_id: this.user_id,
            user: this.user,
            room_id: this.room_id
        }).then(async ()=> {
            await this.resumeServices()
        })
      }.bind(this));
      let thiz = this;
      this.socket.on('rtmpStarted', function (link)  {
        console.log('rtmp Link created  ', link);
        thiz._netLog(`rtmpStarted: processId=${link.processId} userId=${link.userId} (mine=${thiz.user_id})`); // TEMP DIAGNOSTIC
        if (thiz.user_id==link.userId){
            thiz.eventCallback('rtmpStarted', link.processId);
            // A mic analyser used to be built here to pulse #broadcast-profile-border, and it
            // was never torn down: rtmpStarted fires on every publish, so each share switch
            // (camera -> whiteboard above all) left another AudioContext and another
            // requestAnimationFrame loop reading the microphone for the rest of the class.
            // animationFrame holds one id, so exit() could only ever cancel the last of them.
            // Several audio contexts pulling the same track on their own 48kHz clocks is what
            // made the audio break up and jitter, worse after every switch, and Chrome stops
            // handing out contexts after about six. The element it animated is pre-redesign
            // (ActionBoxRow, no longer rendered), so the loop was painting nothing either way.
            // The live meter comes from useMicLevel now, which owns exactly one context and
            // releases it on teardown.
        }
      })
      this.socket.on('rtmpStopped', function (link)  {
        console.log('rtmp Link removed  ', link);
        thiz.eventCallback('rtmpRemoved', link.processId);
        // var x = document.getElementById("snackbar");
        //     x.className = "show";
        //     x.innerHTML = `rtmp stream stopped`
        //     setTimeout(function(){ x.className = x.className.replace("show", ""); }, 3000);
      })

    }

async resumeServices(){
            if (!this.consumerTransport){
                return
            }
            let thiz = this;
            let transport_id = this.consumerTransport.id
            this.socket.request('iceRestart', {transport_id}).then(async (iceParameters) => {
                console.log('iceParameters received for consumer', iceParameters);
                try{
                    await this.consumerTransport.restartIce({iceParameters: iceParameters.iceParameters})
                    console.log('resuming consumer', this.consumers);
                    this.consumers.forEach(async (consumer)=>{
                        await consumer.resume()
                    })
                }
                catch(error){
                    console.log(error);
                }
            }).catch((e)=>{
                thiz.exit()
                console.log(e);
            })

            let producerTransportId = this.producerTransport.id
            console.log(producerTransportId);
            this.socket.request('iceRestart', {transport_id: producerTransportId}).then(async (iceParameters) => {
                console.log('iceParameters received for producer', iceParameters);
                try{
                    await this.producerTransport.restartIce({iceParameters: iceParameters.iceParameters})
                    console.log('resuming producer', this.producers);
                    this.producers.forEach(async (producer)=>{
                        console.log('resume producer',producer.id)
                        await producer.resume()
                    })
                }
                catch(error){
                    console.log(error);
                }
            }).catch((e)=>{
                thiz.exit()
                console.log(e);
            })
}


    //////// MAIN FUNCTIONS /////////////
    async startRecord(fileName){
        try{
            const data  = await this.socket.request('startRecord', {room_id: this.room_id, user_id: this.user_id, fileName});
            console.log('start recording called', data);
        }
        catch(e){
            console.log("error in start recording ",e);
        }
    }

    async stopRecord(){
        try{
            const data = await this.socket.request('stopRecord', {room_id: this.room_id, user_id: this.user_id});
            console.log('stop recording data is',data);
        }
        catch(e){
            console.log("error in the stop recording",e);
        }
    }

    // `videoSource` says where the video comes from when it is not a capture device.
    // 'board' is the whiteboard's own canvas stack, which needs no getDisplayMedia and so no
    // picker — see boardCapture.js. Everything else is unchanged: null still means the camera
    // for 'videoType' and the screen for 'screenType'.
    async produce(type, existing=false, videoSource=null) {
        console.log("existing", existing)
        const mediaConstraints = allmediaConstraints[type]
        const audio = type==='audioType'?true:false
        const board = !audio && videoSource==='board'
        // The label says which producer this is, not what is being sent on it — a screen
        // swapped onto the camera's producer keeps that producer's label. For a fresh
        // capture the label is all there is to go on; for a stream being republished the
        // track itself knows, and is asked below.
        // `screen` is "treat this as detail-first content", not "this is a monitor". The
        // board is text and line art drawn at the window's size, so every choice downstream
        // of here — screen encodings, the 'detail' contentHint, the ladder standing down —
        // is the same one a shared screen wants, and for the same reasons.
        let screen = (type==='screenType' || board) ? true : false
        let producer_id;
        // The canvas capture and its repaint timer, handed to stillTracks below so that
        // clearStillTrack() stops it when something else takes the producer.
        let boardCapture = null;
        if (!this.device?.canProduce('video') && !audio) {
            console.error('cannot produce video');
            return;
        }
        if (this.producerLabel.has(type)&&!existing) {
            console.log('producer already exists for this type ' + type)
            return
        }
        let stream;

        try {
            if (existing){
                producer_id = this.producerLabel.get(type);
                stream = this.allTracks.get(producer_id);
                // A canvas track does not survive a reconnect the way a device track does:
                // reconnectProducer calls closeProducer(type, false), which runs
                // clearStillTrack() on the way out and stops the repaint timer and the track
                // with it. Nothing has to be asked of the teacher though — the board is still
                // on the page — so capture it again rather than republishing an ended track.
                const stashed = stream?.getVideoTracks?.()?.[0]
                if (stashed?.customType === 'board' && stashed.readyState === 'ended'){
                    boardCapture = createBoardStream()
                    stream = boardCapture.stream
                }
            }else if (board){
                // No getUserMedia, no getDisplayMedia, so no prompt of any kind: the board
                // is already on this page and captureStream() reads it straight off.
                boardCapture = createBoardStream()
                stream = boardCapture.stream
            }else{
                stream = screen ? await navigator.mediaDevices.getDisplayMedia(mediaConstraints) : await navigator.mediaDevices.getUserMedia(mediaConstraints)
            }
            
            // Force Microphone Audio Clock to Synchronize with System Clock
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length > 0 && (window.AudioContext || window.webkitAudioContext)) {
                try {
                    const AudioContext = window.AudioContext || window.webkitAudioContext;
                    const audioCtx = new AudioContext();
                    const source = audioCtx.createMediaStreamSource(stream);
                    const destination = audioCtx.createMediaStreamDestination();
                    source.connect(destination);
                    
                    // Replace the drifting hardware track with the system-clock locked track
                    audioTracks.forEach(track => {
                        stream.removeTrack(track);
                        stream.originalHardwareTracks = stream.originalHardwareTracks || [];
                        stream.originalHardwareTracks.push(track);
                    });
                    destination.stream.getAudioTracks().forEach(track => stream.addTrack(track));
                    console.error("🚀🚀🚀 AUDIO CONTEXT SYNC FIX IS OFFICIALLY RUNNING 🚀🚀🚀");
                } catch (e) {
                    console.error("Audio clock sync failed:", e);
                }
            }
 
            const track = audio ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]
            // Republishing a stream that is already open: it was marked when it was captured,
            // and the mark outlives a swap where the label does not. Reading the label alone
            // brought a shared screen back after a reconnect encoded as a camera — motion
            // over detail, and the ladder's 360p rung switched on for a page of code.
            if (existing && !audio && track?.customType){
                // A board being republished after a reconnect is still a board: its canvas
                // track outlives the transport, and reading this as a camera would put it
                // back on the simulcast ladder that swapVideoToBoard stood down.
                screen = track.customType === 'screen' || track.customType === 'board'
            }
            if (!screen && !audio){
                this.eventCallback("cameraOn", stream);
            }
            // A track being republished already knows what it is — keep its own answer rather
            // than recomputing one from `type`, which cannot tell a board from a screen.
            track.customType = audio ? 'audio'
                : (existing && track.customType) ? track.customType
                : board ? 'board' : screen ? 'screen' : 'camera';
            const params = {
                track: track.clone()
            };
            if (audio){
                // The audio producer used to ship with nothing but a track: no encodings, no
                // codecOptions. Everything below this point was gated on `if (!audio)`, so
                // the one stream a class genuinely cannot lose was also the only one sent
                // with no instructions at all.
                //
                // opusFec is the important one. It carries a coarse copy of each packet
                // inside the next, so a single lost packet is reconstructed rather than
                // heard as a gap — which is the difference between a syllable arriving
                // slightly degraded and not arriving. It costs a few kbps on a stream that
                // is already a rounding error next to a 1.5 Mbps screen share.
                //
                // opusDtx is deliberately OFF, and that is the interesting one. DTX stops
                // sending during silence, which on a plain WebRTC call is free bandwidth. It
                // is not free here: this audio is not consumed as WebRTC by anyone. It is
                // bridged to RTMP and transcoded to AAC, and a transcoder wants a continuous
                // stream above all else. DTX hands it a sparse one — a comfort-noise update
                // every few hundred milliseconds instead of a packet every 20ms — and every
                // one of those stretches is a place the AAC timeline can be reconstructed
                // slightly wrong. On a pipeline whose reported symptom is already a drifting
                // audio timeline, saving 30kbps of room tone is the wrong side of that trade.
                //
                // 20ms ptime is the WebRTC default said out loud: larger packets would cut
                // header overhead but add latency, and smaller ones would multiply the
                // per-packet cost on exactly the congested uplink this is meant to survive.
                params.codecOptions = {
                    opusFec: true,
                    opusDtx: false,
                    opusPtime: 20,
                    opusMaxPlaybackRate: 48000,
                };
            }
            if (!audio){
                // The starting rung comes from the measured line speed, so the first frames
                // go out at a rate the connection has already been shown to carry instead of
                // libwebrtc's blind ~300kbps ramp.
                //
                // getSettings() is the capture that actually arrived. The constraints ask for
                // 1080p, but "ideal" is a preference: a 720p webcam returns 720p, and divisors
                // computed for 1080p would then build a 240/320/480 ladder while every label
                // still said 360/480/720.
                const captureHeight = track.getSettings?.()?.height ?? null;
                params.encodings = screen
                    ? screenEncodingsForCapture(captureHeight)
                    : encodingsForCapture(captureHeight);
                this._resetCpuEase();
                // A choice from the quality menu wins over the measurement. Reading only the
                // line speed here is what made manual selection look blocked on a throttled
                // connection: each reconnect republished at the rung the speed test implied,
                // however recently the teacher had picked something else. Auto (_rungPin
                // null) still starts from the measurement.
                const autoRung = this._weakLocked ? 0 : rungForSpeed(this._lineSpeedBps);
                const startRung = Math.min(this._rungPin ?? autoRung,
                                           params.encodings.length - 1);
                params.codecOptions = codecOptionsForRung(startRung);
                if (!screen){
                    this._rungCount = params.encodings.length;
                    this._setActiveRids(startRung);
                }
                // Slides and code are unreadable at a smaller size but perfectly readable at
                // a lower frame rate, so screen share gives up frames and keeps pixels. A
                // camera is the opposite: a sharp still of a talking head is worse than a
                // soft moving one.
                //
                // A single encoding is its own ceiling, whether it is a screen or a camera
                // under LOCK_SINGLE_ENCODING. Announcing the 2.5Mbps top of a ladder that is
                // not being sent leaves libwebrtc probing for headroom the encoding will
                // never be permitted to spend — and probing is itself traffic on the uplink
                // this whole path is trying to keep clear.
                params.codecOptions.videoGoogleMaxBitrate = (screen || params.encodings.length === 1)
                    ? Math.round(params.encodings[0].maxBitrate / 1000)
                    : params.codecOptions.videoGoogleMaxBitrate;
                if (!screen && params.encodings.length > 1){
                    this._videoRung = startRung;
                    this._rungChangedAt = Date.now();
                    // Kept so the controller can tell "the encoder shrank the picture" from
                    // "this camera never had those lines to give".
                    this._captureHeight = Number.isFinite(captureHeight) && captureHeight > 0
                        ? captureHeight : null;
                }else if (!screen){
                    // A camera publishing a single encoding — LOCK_SINGLE_ENCODING, or a
                    // capture too small for a second rung. There is no layer to step between,
                    // so the controller has to stand down the same way it does for a screen:
                    // left with a rung, it would spend the class calling setMaxSpatialLayer on
                    // a sender that has exactly one, which is both pointless and the thing
                    // that churns the transcoder's input resolution.
                    this._videoRung = null;
                    this._rungCount = 1;
                    this._captureHeight = Number.isFinite(captureHeight) && captureHeight > 0
                        ? captureHeight : null;
                }else{
                    // One encoding, and a ladder with no rungs for it to step between. Said
                    // explicitly rather than left at whatever the camera was on: after a
                    // swap to the screen and a reconnect, a stale rung has the controller
                    // asking a single-encoding sender for a spatial layer it does not have.
                    this._videoRung = null;
                    this._rungCount = 1;
                    this._captureHeight = Number.isFinite(captureHeight) && captureHeight > 0
                        ? captureHeight : null;
                }
            }
            let thiz = this;
            console.log("just before check this.producerTransport",this.producerTransport)
            if(!this.producerTransport || (this.producerTransport && this.producerTransport._closed===true)){
                this.producerTransport=null;
                this.consumerTransport=null;
                console.log("producer transport close using the button");
                console.log("producer transport not available")
                const transportCreated = await thiz.initTransports(thiz.device)
                if(!transportCreated){
                    console.log('Failed to join');
                    this.eventCallback('SharePermissionDenied');
                }
            }
            let producer;
            try {
                producer = await this.producerTransport.produce(params)
            } catch (produceError) {
                // Simulcast needs the router to have negotiated the RTP extensions for it.
                // If this deployment has not, publishing must still work: fall back to the
                // single bare encoding this used to send, and say so rather than failing the
                // share. Adaptation then reverts to whatever libwebrtc does on its own.
                if (audio || !params.encodings) throw produceError
                console.log('simulcast rejected, publishing a single encoding', produceError);
                // One encoding, but still scaled: deleting params.encodings outright also
                // deleted the only thing holding the resolution down, so the fallback used to
                // publish the camera or the monitor at its native size.
                const captureHeight = track.getSettings?.()?.height ?? null;
                const source = Number.isFinite(captureHeight) && captureHeight > 0 ? captureHeight : MAX_HEIGHT;
                params.encodings = [{
                    maxBitrate: VIDEO_LADDER[VIDEO_LADDER.length - 1].maxBitrate,
                    scaleResolutionDownBy: Math.max(source / MAX_HEIGHT, 1),
                }];
                delete params.codecOptions;
                this._videoRung = null;
                this._rungCount = 1;
                producer = await this.producerTransport.produce(params)
            }
            if (!audio){
                await this._tuneVideoSender(producer, track, screen, type)
            }else{
                await this._tuneAudioSender(producer)
            }
            // The starting rung was recorded but never asked for. produce() brings every
            // encoding up active, so a restored 360p pin left the sender streaming all three
            // layers while _videoRung, the active rid set and the menu all said 360p — the
            // pin survived the reconnect on paper and not on the wire. Only needed when the
            // rung is below the top; at the top there is nothing to switch off.
            if (!audio && !screen && this._videoRung !== null && this._videoRung < this._rungCount - 1){
                try {
                    await producer.setMaxSpatialLayer(this._videoRung)
                } catch (error) {
                    console.log('setMaxSpatialLayer failed on publish', error);
                }
            }
            // Named so the replacement device track (once recovered below) can re-arm the
            // same recovery on itself if it later ends too, without reassigning the outer
            // `track` const.
            const handleTrackEnded = async (endedTrack) => {
                this._netLog(`track ended: ${endedTrack.customType} (readyState=${endedTrack.readyState})` +
                    (endedTrack.customType === 'screen'
                        ? ' — getDisplayMedia cannot be restarted without a new click'
                        : ''));
                if (endedTrack.customType === 'screen'){
                    // A screen on a producer that was not created for one is a share that was
                    // swapped onto a live class, and that class has a camera to go back to:
                    // ending the capture ends the share, not the broadcast. A producer that
                    // really is the screen has nothing else to publish, so its track ending
                    // is the end of everything.
                    this.eventCallback(type==='screenType' ? 'trackEnded' : 'screenShareEnded')
                    return
                }
                if (endedTrack.customType==='audio'||endedTrack.customType==='camera'){
                    this.eventCallback('audioTrackEnded')
                    // Recover the device that actually ended -- the mic for 'audio', the
                    // webcam for 'camera' -- and push it straight onto the still-live
                    // producer with replaceTrack(). The old version of this handler always
                    // requested { audio: true } even when the CAMERA track had ended, and
                    // only stashed the fresh stream in allTracks without ever applying it to
                    // the producer -- so audio/video stayed dead until, at best, some later
                    // reconnect happened to still be pointing at this exact producer id and
                    // picked the stashed stream up by luck.
                    try{
                        const isAudio = endedTrack.customType === 'audio';
                        const stream = await navigator.mediaDevices.getUserMedia(
                            isAudio ? { audio: true } : { video: true }
                        );
                        const newTrack = isAudio ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];
                        newTrack.customType = endedTrack.customType;
                        if (producer && producer._closed !== true){
                            await producer.replaceTrack({ track: newTrack.clone() });
                            if (!isAudio){
                                await this._tuneVideoSender(producer, newTrack, screen, type);
                            }
                            this._netLog(`track ended: ${endedTrack.customType} recovered — new device applied to the live producer`);
                        }
                        this.allTracks.set(producer.id, stream)
                        // Re-arm recovery on the replacement track in case it also ends later.
                        newTrack.onended = () => handleTrackEnded(newTrack);
                    }catch(error){
                        this._netLog(`track ended: ${endedTrack.customType} — could not get a replacement device: ${error?.message || error}`);
                    }
                }
                console.log("stopped 1009", endedTrack.customType)
            };
            track.onended = () => handleTrackEnded(track);
            // The 3s producer.getStats() probe that used to live here is gone. It read one
            // field — candidate-pair currentRoundTripTime — and emitted 'internetQuality',
            // which drove the "network is poor" banner off round-trip time alone. RTT is
            // latency, not loss: a wifi link can sit at 40ms while dropping 15% of packets
            // (picture breaks up, banner says fine), and a distant relay can sit at 400ms
            // losing nothing (banner cries wolf). It also only existed for video/screen
            // producers, so a mic-only class measured nothing and students measured nothing
            // at all. startNetworkMonitor() reads both transports instead — see there.
            if(interval){
                clearInterval(interval)
            }
            interval = null;
            this.allTracks.set(producer.id, stream)

            this.producers.set(producer.id, producer)

            // The repaint timer belongs to whoever holds the producer, so it is parked with
            // the still frames: clearStillTrack() then stops it on the way past in
            // swapVideoToScreen/swapVideoToCamera, instead of leaving a canvas being redrawn
            // fifteen times a second for a track nothing is sending any more.
            if (boardCapture){
                this.stillTracks.set(producer.id, { stillTrack: boardCapture.track, timer: boardCapture.timer })
            }

            producer.on('trackended', () => {
                console.log("server log: the trackended")
                // thiz.eventCallback('trackEnded')
            })

            producer.on('transportclose', () => {
                let thiz = this
                console.log('producer transport close', this.allTracks.get(producer.id))
                thiz._netLog(`producer '${track.customType}' died with the transport`);
                thiz.eventCallback('producerTransportClose')

            })

            producer.on('close', () => {
                console.log('closing producer')
                this.producers.delete(producer.id)

            })

            this.producerLabel.set(type, producer.id)

        } catch (err) {
            if (err.name=='NotAllowedError'){
                this.eventCallback('SharePermissionDenied');
            }
            console.log("982", err)
            if (stream) {
                this.endTracks(stream);
            }
        }
    }

    async consume(producer_id, user_id) {

        // let info = await this.roomInfo()
        // console.log('roomInfo', info);
        
        console.log('tring to consume', producer_id, user_id);
        
        this.getConsumeStream(producer_id, user_id).then(async function ({
            consumer,
            stream,
            kind,
            userName
        }) {
            // let stats = await consumer.getStats()
            // for (const now of stats.values()) {
            //     console.log(now);
            // }
            console.log('got stream ', consumer, stream, kind, producer_id, userName);
            
            this.consumers.set(consumer.id, consumer)
            // A consumer created after the choice was made has to be told about it too,
            // or switching to 360p would hold only until the next person shared something.
            this._applyConsumerLayers(consumer)

            let elem;
            if (kind === 'video') {
                // elem = document.createElement('video')
                // elem.srcObject = stream
                // elem.id = consumer.id
                // elem.playsinline = false
                // elem.autoplay = true
                // elem.className = "vid"
                // this.remoteVideoEl.appendChild(elem)
            } else {
                // elem = document.createElement('audio')
                // elem.srcObject = stream
                // elem.id = consumer.id
                // elem.playsinline = false
                // elem.autoplay = true
                // this.remoteAudioEl.appendChild(elem)
            }

            consumer.on('trackended', function () {
                this.removeConsumer(consumer.id)
            }.bind(this))
            consumer.on('transportclose', function () {
                console.log('consumer transport closed');
                
                this.removeConsumer(consumer.id)
            }.bind(this))



        }.bind(this)).catch(e=> console.log('consume error', e))
    }

    async getConsumeStream(producerId, user_id) {
        const {
            rtpCapabilities
        } = this.device
        const data = await this.socket.request('consume', {
            rtpCapabilities,
            consumerTransportId: this.consumerTransport.id, // might be 
            producerId,
            user_id
        });
        const {
            id,
            kind,
            rtpParameters,
            userName
        } = data;

        let codecOptions = {};
        const consumer = await this.consumerTransport.consume({
            id,
            producerId,
            kind,
            rtpParameters,
            codecOptions,
        })
        const stream = new MediaStream();
        stream.addTrack(consumer.track);
        return {
            consumer,
            stream,
            kind,
            userName
        }
    }
    endTracks(stream){
        stream?.getTracks().forEach(function (track) {
            track.stop()
        })
        if (stream && stream.originalHardwareTracks) {
            stream.originalHardwareTracks.forEach(function (track) {
                track.stop();
            });
        }
    }
    mockcloseProducer(type){
        if(interval){
            clearInterval(interval)
        }
        interval = null;
        if (!this.producerLabel.has(type)) {
            console.log('there is no producer for this type ' + type)
            return
        }
        let producer_id = this.producerLabel.get(type)
        console.log(producer_id)
        // this.socket.emit('producerClosed', {
        //     producer_id
        // })
        this.producers.get(producer_id)?.close()
    }

    closeProducer(type, endTracks=true) {
        if(interval){
            clearInterval(interval)
        }
        interval = null;
        if (!this.producerLabel.has(type)) {
            console.log('there is no producer for this type ' + type)
            return
        }
        let producer_id = this.producerLabel.get(type)
        console.log(producer_id)
        // endTracks is the difference between "this can come back silently" and "the
        // teacher has to click again": stopping a getDisplayMedia track is irreversible
        // without a fresh user gesture.
        this._netLog(`closeProducer(${type}) endTracks=${endTracks} <- ${this._caller()}`);
        this.socket.emit('producerClosed', {producer_id})
        this.producers.get(producer_id)?.close()
        this.producers.delete(producer_id)
        this.clearStillTrack(producer_id)
        if (endTracks){
            this.producerLabel.delete(type)
            this.endTracks(this.allTracks.get(producer_id))
        }
    }

    reconnectProducer(type){
        // Restore everything that was live, not just audio. Closing videoType/screenType
        // here and never reproducing them (the old behaviour, see the removed "WILL NOT
        // RETURN" log) left the camera/screen permanently gone after the first drop -- and,
        // since the app keeps re-detecting that as broken (wrong consumer count / rtmp
        // monitoring), kept calling this again every couple of seconds forever. produce()
        // below is called with existing=true, so this reuses the still-open tracks captured
        // before the disconnect, the same way audio always has, rather than asking the user
        // for new camera/screen permission.
        const live = ['audioType', 'videoType', 'screenType'].filter((each) => this.producerLabel.has(each));
        this._netLog(`reconnectProducer(${type}) <- ${this._caller()}`);
        this._netLog(`  live now: [${live.join(', ')}] — restoring all of them`);
        live.forEach((each) => this.closeProducer(each, false));
        // audioType's own produce() is what index.js watches (audioAdded/rtmpStarted) to know
        // the reconnect landed; index.js skips its own video/screen restore attempt while a
        // reconnect is in flight now that this restores them directly (isAutoReconnectingRef).
        live.forEach((each) => this.produce(each, true));
    }

    mute(){
        // console.log("mute called in the room client");
        const audioProduerId = this.producerLabel.get("audioType") 
        // console.log("audioProducer Id is", audioProduerId);
        const audioProducer  = this.producers.get(audioProduerId);
        audioProducer?.pause();
        this.event(_EVENTS.mute)
    }
    unMute(){
        const audioProduerId = this.producerLabel.get("audioType") 
        // console.log("audioProducer Id is", audioProduerId);
        const audioProducer  = this.producers.get(audioProduerId);
        audioProducer?.resume();
        this.event(_EVENTS.unMute)
    }

    // Turning the camera off has to release the device: its light stays on for as long as
    // any track from it is live, and producer.pause() only sets track.enabled = false. So
    // move the producer onto a still canvas track and stop the real one. The producer, its
    // transport, the RTMP link and any running recording all survive — consumers simply
    // receive a still frame until the camera comes back.
    // `painter` draws the frame consumers and any recording will see while the camera is
    // off. Without one this is a flat rectangle, which is what made a camera-off stretch of
    // a recording play back as a black screen — the caller passes the same stage frame the
    // class shows instead.
    async swapVideoToStill(type='videoType', painter=null, color='#1e1a2e'){
        if (!this.producerLabel.has(type)) return false
        const producer_id = this.producerLabel.get(type)
        const producer = this.producers.get(producer_id)
        if (!producer || producer._closed===true) return false
        const canvas = document.createElement('canvas')
        canvas.width = 1280
        canvas.height = 720
        const paint = () => {
            if (painter){
                painter(canvas)
                return
            }
            const context = canvas.getContext('2d')
            context.fillStyle = color
            context.fillRect(0, 0, canvas.width, canvas.height)
        }
        paint()
        const stillStream = canvas.captureStream(1)
        const stillTrack = stillStream.getVideoTracks()[0]
        // a canvas nothing draws to can stop handing frames to its capture stream
        const timer = setInterval(paint, 1000)
        await producer.replaceTrack({ track: stillTrack })
        // Whatever canvas was on this producer before — an earlier still frame, or the
        // whiteboard — owns a repaint timer that the set() below would otherwise orphan.
        this.clearStillTrack(producer_id)
        this._releasePreviousTrack(producer_id)
        this.stillTracks.set(producer_id, { stillTrack, timer })
        this.allTracks.set(producer_id, stillStream)
        return true
    }

    // maintain-resolution keeps text legible when a shared screen runs short of bandwidth;
    // the camera keeps its frame rate instead. Wrapped because the property is read-only on
    // older senders. Called on every track change now, not only at produce() time: a
    // producer that starts on the camera and is later swapped to a screen needs the screen's
    // answer to both of these, and one swapped back needs the camera's.
    async _tuneVideoSender(producer, track, screen, type='videoType'){
        // Which budget table applies from here on, and a budget starting from nominal for
        // whatever has just been put on this producer. Recorded only for the camera's
        // producer, and keyed on the label rather than the id because produce() has not
        // registered the id yet when it calls this: a dedicated 'screenType' producer is a
        // second sender the budget controller never looks at, and letting it set this would
        // have the controller adapt the camera against a screen's steps.
        if (type === 'videoType'){
            this._sendingScreen = !!screen;
            this._resetVideoBudget();
        }
        try {
            const sender = producer.rtpSender;
            if (sender){
                const parameters = sender.getParameters();
                // maintain-resolution for the camera too, which it was not.
                //
                // Switching off simulcast stops the *server* changing which resolution it
                // forwards, but it does not stop the encoder shrinking the picture on its own
                // — and 'maintain-framerate' is a standing instruction to do exactly that
                // whenever the uplink tightens. Every one of those is another input geometry
                // change at the transcoder, so leaving it here would have left half the
                // problem in place while looking like it was solved.
                //
                // The trade it makes is the honest one for a lecture. A talking head that
                // holds 480p and drops to a lower frame rate is still perfectly watchable; a
                // picture that keeps changing size takes the audio down with it, because
                // every resize costs a scale-graph rebuild in three ffmpeg processes at once.
                parameters.degradationPreference = 'maintain-resolution';
                this._rescaleEncodings(parameters, track, screen);
                // And the budget's other half. _rescaleEncodings puts the bitrate back to
                // the head of the step table, but nothing there touches maxFramerate — so a
                // 15fps cap earned during the last track's worst thirty seconds would have
                // followed the new one onto the wire and stayed until the budget happened
                // to move again. Cleared rather than set, because step 0 has no cap; the
                // CPU ease is the one reason a fresh track may still start with one.
                parameters.encodings?.forEach((encoding) => {
                    if (this._cpuEased) encoding.maxFramerate = CPU_EASED_FPS;
                    else delete encoding.maxFramerate;
                });
                await sender.setParameters(parameters);
            }
            if (track.contentHint !== undefined){
                track.contentHint = screen ? 'detail' : 'motion';
            }
        } catch (error) {
            console.log('sender not retuned for the new track', error);
        }
    }

    // Tell the pacer that audio does not wait.
    //
    // Audio and video share one bundled transport, so they share one send queue. Nothing
    // marked which of the two mattered, and the screen share is the noisy neighbour: a
    // single encoding under 'maintain-resolution', which is an instruction never to shrink
    // the picture under pressure. It holds its pixels, the queue grows, and the audio sitting
    // behind it arrives late and bunched — heard as jitter, and the reason the complaint is
    // always "when I share".
    //
    // networkPriority is the knob for that, and it has to be set here rather than passed to
    // produce(): it lives on the RTCRtpSender's encoding parameters, which is the same route
    // _tuneVideoSender already takes to reach degradationPreference. `priority` is the older
    // spelling of the same field and is set alongside it for browsers still reading that one.
    //
    // No maxBitrate. Opus picks its own rate, sits around 32-64kbps for speech, and capping
    // it here would take away the headroom in-band FEC needs to do anything.
    async _tuneAudioSender(producer){
        try {
            const sender = producer.rtpSender;
            if (!sender) return
            const parameters = sender.getParameters();
            // setParameters rejects a different encoding count than getParameters returned,
            // so this only ever edits what is already there rather than adding one.
            if (!parameters.encodings || !parameters.encodings.length) return
            parameters.encodings.forEach((encoding) => {
                encoding.networkPriority = 'high';
                encoding.priority = 'high';
            });
            await sender.setParameters(parameters);
        } catch (error) {
            console.log('audio sender not retuned', error);
        }
    }

    /*
     * Point the existing encodings at the size the new track actually captures.
     *
     * scaleResolutionDownBy is a divisor, not a target. The factors produce() worked out are
     * a statement about the capture it was handed — a 720p webcam gives the top rung a
     * divisor of 1 — and a swap hands those same factors a different capture. Put a 1440p
     * monitor behind them and the top rung goes out at 1440p under the name 720p, at 720p's
     * bitrate: the one case where a screen share looks worse than the camera it replaced.
     * Recompute each rung against the new capture so it carries the size it is called.
     *
     * This is the same arithmetic encodingsForCapture does, so it is a no-op when the track
     * has not changed shape — including every call from produce(), where the encodings were
     * just built for this exact track.
     */
    _rescaleEncodings(parameters, track, screen){
        const height = track.getSettings?.()?.height;
        const source = Number.isFinite(height) && height > 0 ? height : null;
        const encodings = parameters?.encodings;
        if (!source || !encodings?.length) return
        // Every rung has to be recognisable before any of them is touched. A browser that
        // returns encodings without rids would otherwise have all three read as the top one
        // and the whole ladder collapse onto a single size. One encoding needs no rid — the
        // screen's single stream and the simulcast fallback both stand for the top.
        const named = encodings.length === 1
            || encodings.every((encoding) => VIDEO_LADDER.some((rung) => rung.rid === encoding.rid));
        if (!named) return
        // maxBitrate moves with the size, and it did not before — which is the whole of the
        // "jitter when sharing" report. A share is a replaceTrack on the camera's producer,
        // so the encoding it lands on is the camera's: LOCKED_RUNG's 400kbps. Only the
        // divisor was being recomputed, and for a ridless encoding it resolved to 720p — so
        // pressing Share quadrupled the pixels and left the budget where it was. Under
        // 'maintain-resolution' the encoder may not answer that by shrinking, so the only
        // lever it has left is frame rate: a 720p board at 400kbps crawls, unevenly, and
        // that unevenness is what a class sees at the far end.
        //
        // Setting both together means an encoding always carries a size and a budget that
        // were chosen for each other, whichever track is on it and however it got there.
        const single = singleEncodingTarget(screen);
        encodings.forEach((encoding) => {
            const rung = VIDEO_LADDER.find((each) => each.rid === encoding.rid);
            const target = Math.min(rung?.height ?? single.height, MAX_HEIGHT);
            encoding.scaleResolutionDownBy = Math.max(source / target, 1);
            encoding.maxBitrate = rung?.maxBitrate ?? single.maxBitrate;
        });
    }

    // What the source can actually supply, which the ladder reads to tell a frame the encoder
    // shrank from one the source never had the lines for. A swap changes the source, so a
    // stale reading here makes a screen share look like a failing camera or the reverse.
    _recordCaptureHeight(track){
        const height = track.getSettings?.()?.height;
        this._captureHeight = Number.isFinite(height) && height > 0 ? height : null;
    }

    // Stops whatever the producer was sending before a swap put something else on it.
    // getTracks, not getVideoTracks: a stream from getUserMedia can carry a microphone track
    // too, and leaving that capture open stacks another one on every swap. onended is cleared
    // first — it republishes when a device drops out on its own, and this stop is asked for.
    _releasePreviousTrack(producer_id){
        const previous = this.allTracks.get(producer_id)
        previous?.getTracks?.().forEach((track) => {
            track.onended = null
            track.stop()
        })
    }

    // The screen share, as a track swap on the video producer that is already live rather
    // than a producer of its own.
    //
    // A second producer, or closing this one and opening a 'screenType', both end the RTMP
    // link the server raised around this producer and restart the whole publish — that is
    // the teardown a teacher sees as "the stream stopped and I had to share again", and the
    // moment audio and video stop being one timeline. replaceTrack keeps the producer, its
    // id, its SSRC and its transport exactly where they are, so the server never learns
    // anything changed: the audio producer is untouched, the RTP timeline is continuous, and
    // the two stay in sync because neither was restarted. The producer's label does not
    // change either — it stays whatever it was created as, and index.js tracks
    // screen-vs-camera separately from it.
    async swapVideoToScreen(type='videoType'){
        if (!this.producerLabel.has(type)) return false
        const producer_id = this.producerLabel.get(type)
        const producer = this.producers.get(producer_id)
        if (!producer || producer._closed===true) return false
        // Throws NotAllowedError when the picker is cancelled. Nothing has been touched by
        // then, so the camera simply carries on and the caller only has to say so.
        const stream = await navigator.mediaDevices.getDisplayMedia(allmediaConstraints['screenType'])
        const track = stream.getVideoTracks()[0]
        track.customType = 'screen'
        // The browser's own "Stop sharing" bar. It ends the screen half of the class, not the
        // class, so this asks for a swap back rather than the full teardown produce()'s
        // screen track triggers.
        track.onended = () => { this.eventCallback('screenShareEnded') }
        await producer.replaceTrack({ track: track.clone() })
        this.clearStillTrack(producer_id)
        this._releasePreviousTrack(producer_id)
        this.allTracks.set(producer_id, stream)
        this._recordCaptureHeight(track)
        // Stand the ladder down, exactly as produce() does when it creates a screen producer
        // outright. It did not happen here, and this is the path the Share button actually
        // takes — so in practice every screen share ran with AUTO still climbing and dropping
        // rungs underneath it.
        //
        // That is not a cosmetic inconsistency. Every rung change moves which spatial layer
        // the server forwards, the RTMP bridge's ffmpeg sees its input resolution change, and
        // with reinit_filter on it rebuilds its scale graph: video stalls for that moment
        // while audio keeps arriving, the muxing queues go out of balance, and the audio that
        // comes out the far side carries a discontinuous presentation timeline. The samples
        // are fine. Chrome's MSE rejects the timeline strictly and cracks; ffmpeg- and
        // VLC-based players absorb it silently — which is exactly the pattern reported, right
        // down to the same stream sounding clean in an external player.
        //
        // A shared screen has nothing to gain from the ladder anyway: it publishes for
        // legibility, gives up frame rate rather than size, and produce() already treats it
        // that way. The rung is remembered so the swap back can resume where it left off
        // rather than restarting the climb from a measurement taken minutes ago.
        this._rungBeforeShare = this._videoRung
        this._videoRung = null
        this._rungChangedAt = Date.now()
        await this._tuneVideoSender(producer, track, true, type)
        this.eventCallback('screenOn', stream)
        return true
    }

    // The whiteboard, as a track swap on the video producer that is already live.
    //
    // Everything swapVideoToScreen says about replaceTrack holds here for the same reasons:
    // the producer, its id, its SSRC, its transport and the RTMP link the server raised
    // around it all stay exactly where they are, and the audio producer is never touched.
    // What differs is where the picture comes from. The board is a canvas already on this
    // page, so captureStream() reads it directly — no getDisplayMedia, no picker, no user
    // gesture to spend, and no window for the teacher to have to pick themselves out of.
    async swapVideoToBoard(type='videoType'){
        if (!this.producerLabel.has(type)) return false
        const producer_id = this.producerLabel.get(type)
        const producer = this.producers.get(producer_id)
        if (!producer || producer._closed===true) return false
        const capture = createBoardStream()
        const track = capture.track
        if (!track){
            capture.stop()
            return false
        }
        await producer.replaceTrack({ track: track.clone() })
        // Whatever this producer was carrying — a still frame, the camera, a shared screen —
        // is finished with. clearStillTrack first so the entry this capture is about to take
        // is free, then release the device capture the old track came from.
        this.clearStillTrack(producer_id)
        this._releasePreviousTrack(producer_id)
        this.allTracks.set(producer_id, capture.stream)
        this.stillTracks.set(producer_id, { stillTrack: track, timer: capture.timer })
        this._recordCaptureHeight(track)
        // Stand the ladder down, exactly as the screen share does and for exactly the same
        // reason: every rung change moves which spatial layer the server forwards, ffmpeg on
        // the RTMP bridge rebuilds its scale graph around the new input size, and the audio
        // that comes out the far side carries a discontinuous presentation timeline. A board
        // has nothing to gain from the ladder anyway — it publishes for legibility, and it is
        // already capped at 720p15 by boardCapture.
        this._rungBeforeShare = this._videoRung
        this._videoRung = null
        this._rungChangedAt = Date.now()
        await this._tuneVideoSender(producer, track, true, type)
        this.eventCallback('boardOn', capture.stream)
        return true
    }

    async swapVideoToCamera(type='videoType'){
        if (!this.producerLabel.has(type)) return false
        const producer_id = this.producerLabel.get(type)
        const producer = this.producers.get(producer_id)
        if (!producer || producer._closed===true) return false
        // Keyed to 'videoType' rather than to `type`: the label says which producer this is,
        // not what is on it, so a producer created as a screen share and swapped to the
        // camera would otherwise hand getUserMedia the screen capture's constraints.
        let stream = null
        try {
            stream = await navigator.mediaDevices.getUserMedia(allmediaConstraints['videoType'])
        } catch (error) {
            // The device would not open. Coming back from a board or a screen share there is
            // an overlay capture of that same camera already running in this tab, and
            // adopting it is a far better answer than failing the swap: the teacher keeps a
            // picture, at whatever resolution the overlay was opened with, instead of being
            // left with the share closed and nothing on the wire. Cleared off the client as
            // it is taken over, so the stopcamera() that would normally release the overlay
            // cannot stop the track the producer is now sending.
            const overlay = this.camerastream
            const overlayTrack = overlay?.getVideoTracks?.()?.[0]
            if (!overlayTrack || overlayTrack.readyState !== 'live') throw error
            this.camerastream = null
            stream = overlay
        }
        const track = stream.getVideoTracks()[0]
        track.customType = 'camera'
        // same recovery produce() wires up, so a device that dies later still republishes
        track.onended = () => { this.eventCallback('audioTrackEnded') }
        await producer.replaceTrack({ track: track.clone() })
        this.clearStillTrack(producer_id)
        // Coming back from a screen share this is the display capture, and leaving it live is
        // what keeps Chrome's "you are sharing your screen" bar up after the share has ended.
        this._releasePreviousTrack(producer_id)
        this.allTracks.set(producer_id, stream)
        this._recordCaptureHeight(track)
        // Back on the camera, so the ladder has something to adapt again. Resumed at the rung
        // the share interrupted rather than recomputed, because the producer's encodings never
        // changed across the swap — restarting from a stale line-speed reading would ask the
        // sender for a layer it is not currently on and produce the very resolution change
        // this pair of methods now exists to avoid.
        //
        // Only if this producer has rungs to resume onto. Under LOCK_SINGLE_ENCODING the
        // share interrupted a camera that was already ladderless, so _rungBeforeShare is
        // null and the fallback below handed back a rung index for a sender with one
        // encoding — which left _adaptVideoRung routed to the ladder controller, where it
        // could do nothing, instead of to the budget controller, which can.
        this._videoRung = this._rungCount > 1
            ? (this._rungBeforeShare !== null && this._rungBeforeShare !== undefined
                ? this._rungBeforeShare
                : (this._rungPin ?? rungForSpeed(this._lineSpeedBps)))
            : null
        this._rungBeforeShare = null
        this._rungChangedAt = Date.now()
        await this._tuneVideoSender(producer, track, false, type)
        this.eventCallback('cameraOn', stream)
        return true
    }

    clearStillTrack(producer_id){
        const still = this.stillTracks.get(producer_id)
        if (!still) return
        clearInterval(still.timer)
        still.stillTrack?.stop()
        this.stillTracks.delete(producer_id)
    }

    pauseProducer(type) {
        if (!this.producerLabel.has(type)) {
            console.log('there is no producer for this type ' + type)
            return
        }
        let producer_id = this.producerLabel.get(type)
        this.producers.get(producer_id).pause()

    }

    pauseProducerRemote(producer_id){
        this.producers.get(producer_id).pause()
    }

    resumeProducerRemote(producer_id){
        this.producers.get(producer_id).resume()
    }

    resumeProducer(type) {
        if (!this.producerLabel.has(type)) {
            console.log('there is no producer for this type ' + type)
            return
        }
        let producer_id = this.producerLabel.get(type)
        this.producers.get(producer_id).resume()

    }

    removeConsumer(consumer_id) {
        this.consumers.delete(consumer_id)
    }

    closeTransport(){
        this.producerTransport?.close()
    }
    exitRoom(){
        let clean = function () {
            this._isOpen = false
            this.consumerTransport?.close()
            this.producerTransport?.close()
            this.socket?.disconnect();
        }.bind(this)
        this.socket.request('exitRoom').then(e => console.log(e)).catch(e => console.warn(e)).finally(function () {
            clean()
        }.bind(this))
    }
    exit() {
        if (interval){
            clearInterval(interval)
        }
        interval = null
        this.stopNetworkMonitor()
        window.removeEventListener("resize", updateSize);
        if (animationFrame){
            cancelAnimationFrame(animationFrame)
            animationFrame=false
        }
        this.closeProducer('audioType')
        this.closeProducer('screenType')
        this.closeProducer('videoType')

        if (this.allTracks) {
            for (let stream of this.allTracks.values()) {
                this.endTracks(stream);
            }
            this.allTracks.clear();
        }

        if (this.producers) {
            for (let producer of this.producers.values()) {
                if (producer && producer.track) {
                    producer.track.stop();
                }
            }
        }

        let clean = function () {
            this._isOpen = false
            this.consumerTransport?.close()
            this.producerTransport?.close()
            this.socket?.disconnect();
            console.log('cleaning socket');
        }.bind(this)

        if (!this.offline) {
            this.socket.request('exitRoom').then(e => console.log(e)).catch(e => console.warn(e)).finally(function () {
                clean()
            }.bind(this))
        } else {
            clean()
        }
        this.event(_EVENTS.exitRoom)

    }

    disconnect(){
        this.socket.disconnect();
    }

    connect(){
        this.socket.connect();
    }
    ///////  HELPERS //////////

    async roomInfo() {
        let info = await socket.request('getMyRoomInfo', {a:1, b:2, c:3})
        return info
    }

    static get mediaType() {
        return mediaType
    }

    event(evt) {
        if (this.eventListeners.has(evt)) {
            this.eventListeners.get(evt).forEach(callback => callback())
        }
    }

    on(evt, callback) {
        this.eventListeners.get(evt).push(callback)
    }

    //////// GETTERS ////////

    isOpen() {
        return this._isOpen
    }

    static get EVENTS() {
        return _EVENTS
    }

    startLocalRecording(type=videoType){
        const audio_producer_id = this.producerLabel.get('audioType');
        const video_producer_id = this.producerLabel.get(type);
        const audio_stream = this.allTracks.get(audio_producer_id);
        const additionalVideoTracks = this.allTracks.get(video_producer_id);
        const additionalAudioTracks = audio_stream.getAudioTracks();
        recorder.startLocalRecording({additionalVideoTracks, additionalAudioTracks, roomname:this.room_id});
    }
    stopLocalRecording(){
        recorder.stopLocalRecording();
    }
    pauseLocalRecording(){
        recorder.pauseLocalRecording();
    }
    resumeLocalRecording(){
        recorder.resumeLocalRecording();
    }
}
export default RoomClient;